import React, { useState, useEffect, Fragment, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTranslatableData } from '../../hooks/useTranslatable';
import MenuHeader from "../MenuHeader";
import Modal from "react-modal";
import BookingService from "../../services/booking.service";
import UserService from "../../services/user.service";
import styles from './ListTable.module.css';
import { addressFormat, asTitle, castDate, castDateshort } from "../../shared/utils/string";
import useApi from "../../hooks/useApi";
import useAuth from "../../hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import CreateTaskModal from "./../CreateBookingModal";
import CreateTaskManagerModal from "./../CreateBookingManagerModal";
import { Menu, Chip, MenuList, MenuItem, IconButton, ListItemText, ListItemIcon, Divider, createTheme, ThemeProvider, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText } from '@mui/material';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { event } from "jquery";
import EditIcon from '@mui/icons-material/Edit';
import StatusBadge from "./StatusBadge.js";
import FicheClientService from "../../services/fiche_client.service";
import PDFFile from './../../components/PDFFile';
import { PDFViewer } from "@react-pdf/renderer";
import { pdf } from "@react-pdf/renderer"; // Make sure to import pdf
import FicheHotelService from "../../services/fiche_hotel.service";
import HotelService from "../../services/hotel.service";
import { useNotifications } from '../../context/NotificationProvider';
import PromotionService from "../../services/promotion.service";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import EditBookingModal from '../../components/BookingEditModal';
import BookingEditManagerModal from '../../components/BookingEditManagerModal';
import AdminFlexibleCancellationModal from '../AdminFlexibleCancellationModal';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GeneralSettingService from '../../services/general_setting.service';
import stylesCommande from '../Commande/Commande.module.css';
import ProductService from "../../services/product.service";
import CloseIcon from '@mui/icons-material/Close';
import TimerIcon from '@mui/icons-material/Timer';
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import HistoryIcon from '@mui/icons-material/History';
import Papa from 'papaparse';
import { bindEvent, getPusher, initPusher } from "../../services/pusher.service";
import { getClientBookingsCount, getHotelClientBadgeLabel, isHotelRoomBooking, isInternalHotelClient, isNewClient, isVipClient } from "../../shared/utils/clientDisplay";
import { getFlyproDisplayName, hasFlyproPreference } from "../../shared/utils/bookingPreference";

const theme = createTheme({
  palette: {
    white: {
      "main": "#FFFFFF",
      "light": "#FFFFFF",
      "dark": "#FFFFFF",
      "contrastText": "#FFFFFF"
    },
    dark_yellow: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FAEF9B",
    },
    dark_white: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#FFFFFF",
    },
    dark: {
      "main": "#393D44",
      "light": "#393D44",
      "dark": "#393D44",
      "contrastText": "#393D44"
    }
  },
});

const compactClientBadgeSx = {
  mt: 0.3,
  fontSize: "0.65rem",
  height: 18,
  "& .MuiChip-label": {
    px: 0.5,
    py: 0,
  },
};

const HotelClientBadges = ({ booking, t }) => (
  <>
    <Chip
      label={getHotelClientBadgeLabel(booking, t)}
      color={isInternalHotelClient(booking) ? "primary" : "secondary"}
      size="small"
      variant="outlined"
      sx={compactClientBadgeSx}
    />
    {isHotelRoomBooking(booking) && (
      <Chip
        label="IN ROOM"
        color="warning"
        size="small"
        sx={{ ...compactClientBadgeSx, ml: 0.5 }}
      />
    )}
  </>
);

Modal.setAppElement("#root"); // Ensure this matches your root element

function getExtendedEndTime(endBeforeISO, durationMin) {
  if (!endBeforeISO || !durationMin) return null;
  try {
    const utc = new Date(endBeforeISO);
    if (isNaN(utc)) return null;
    const zurichTimeStr = utc.toLocaleString("en-US", { timeZone: "Europe/Zurich" });
    const zurichDate = new Date(zurichTimeStr);
    if (isNaN(zurichDate)) return null;
    return new Date(zurichDate.getTime() + durationMin * 60000);
  } catch (e) {
    console.error("getExtendedEndTime error:", e);
    return null;
  }
}

const PAYMENT_LINK_RESEND_STATUSES = new Set(['CREATED', 'PROCESSING_PAYMENT', 'FAILED_PAYMENT']);

function canResendPaymentLink(booking) {
  return Boolean(
    booking
    && !booking.offline_payment
    && PAYMENT_LINK_RESEND_STATUSES.has(booking.status)
  );
}

function hasMixedPaidBookingProducts(booking) {
  const rows = Array.isArray(booking?.booking_products) && booking.booking_products.length
    ? booking.booking_products
    : booking?.products || [];

  return rows.some(p => p.is_paid === false) && rows.some(p => p.is_paid === true);
}

const Booking = ({ onViewChange, selected, showHeader = true }) => {
  const handleButtonClick = (view) => {
    onViewChange(view);
  };

  const { t, i18n } = useTranslation(['dashboard', 'commande', 'bill']);
  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      overflow: "hidden auto",
      scrollbarWidth: "none",
      height: "650px",
    },
  };

  const [activeTabModal, setActiveTabModal] = useState('details');

  const customStylesExp = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

  const location = useLocation();

  const api = useApi();

  const [bookings, setBookings] = useState([]);
  const [flypros, setFlys] = useState([]);

  // Use translated bookings for display
  const translatedBookings = useTranslatableData(bookings, ['services.product.name', 'services.product.category.title']);
  const [filterData, setFilterData] = useState({
    search: "",
    search_by: "client.extra.full_name",
    operator: "contains",
    status: "",
  });
  const { auth } = useAuth();
  const userRoles = auth?.roles;
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const isPrivileged = userRoles?.some(role => role.name === "ROLE_ADMIN" || role.name === "ROLE_HOTEL_MANAGER");
  const isHotelManager = userRoles?.some(role => role.name === "ROLE_HOTEL_MANAGER");
  const isAdmin = userRoles?.some(role => role.name === "ROLE_ADMIN");
  const [filters, setFilters] = useState({
    with: `ratings,address,client.roles,client.ficheClient,client_with_bookings_count,products.category,booking_products,creator,editor,therapists,therapists.media,id,transactions,hotel,hotel.ficheHotel,hotel.address,histories,histories.editor,histories.creator`,
    page: currentPage,
    per_page: perPage,
    id: id ? `in:${id}` : "",
    sort: '-created_at',
    ...(!isPrivileged && { created_by: `eq:${auth?.user?.id}` })
  });
  const notify = useNotifications();
  // For prolongation modal
  const [showProlongModal, setShowProlongModal] = useState(false);
  const [bookingToProlong, setBookingToProlong] = useState(null);
  const [availableProlongProducts, setAvailableProlongProducts] = useState([]);
  const [selectedProlongProduct, setSelectedProlongProduct] = useState(null);
  const [prolongErrorMessage, setProlongErrorMessage] = useState('');
  const [prolongationErrorMessage, setProlongationErrorMessage] = useState("");
  const [isProlongLoading, setIsProlongLoading] = useState(false);
  const [extendedEndTime, setExtendedEndTime] = useState(null);
  const [originalEndTime, setOriginalEndTime] = useState(null);

  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState(true);
  const [detailsModalIsOpen, setDetailsModalIsOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const timeoutRef = useRef(null);
  const bookingsRefreshRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const lastRefreshRef = useRef(0);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElStatus, setAnchorElStatus] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState({ item: null, newStatus: null, justification: null });
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [bookingToDuplicate, setBookingToDuplicate] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCancelPopup, setShowCancelPopup] = useState(false);



  const [ficheFormData, setFicheFormData] = useState({
    id: '',
    user_id: '',
    parking: '',
    maison_ou_immeuble: '',
    infos_logistiques: '',
    tensions_particulieres: '',
    pression_ideale: '',
    huile_preferee: '',
    type_musique: '',
    problemes_physique: '',
    parties_preferees: '',
    parties_a_ne_pas_masser: '',
    se_fait_masser_nu: '',
    mauvaise_hygiene: '',
    remarque_deplacee: '',
    age: '',
    type_profession: '',
    source: '',
    classe_sociale: '',
    parking_on: '',
    infos_logistiques_on: '',
    maison_ou_immeuble_on: '',
    problemes_physique_on: '',
    couleur_on: '',
    forme_on: '',
    cuticules_on: '',
    ongle_on: '',
    age_on: '',
    type_on: '',
    entendu_on: '',
    classe_on: '',
    info_on: '',
    info_complementaires: '',
  });

  function asTitles(status) {
    return t(`status.${status}`);
  }

  const [detailsModalIsOpenFiche, setDetailsModalIsOpenFiche] = useState(false);
  const [exportModalIsOpen, setExportModalIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState("");
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelChoicePhase, setCancelChoicePhase] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState("");
  const [showAdminCancelModal, setShowAdminCancelModal] = useState(false);
  const [bookingForAdminCancel, setBookingForAdminCancel] = useState(null);

  const canAdminFlexibleCancel = (booking) => Boolean(
    isAdmin
    && booking
    && !['CANCELED', 'DECLINED', 'COMPLETED'].includes(booking.status)
  );

  const openAdminFlexibleCancellation = (booking) => {
    setBookingForAdminCancel(booking);
    setShowAdminCancelModal(true);
  };

  const closeAdminFlexibleCancellation = () => {
    setShowAdminCancelModal(false);
    setBookingForAdminCancel(null);
  };

  const handleAdminFlexibleCancellationSuccess = async () => {
    await fetchBookings();
  };

  const getDeleteWarning = (item) => {

    if (item.status === "CANCELED" || item.status === "COMPLETED") {
      return "";
    }

    if (item.offline_payment === true) {
      return t("dashboard:menu.offlinePaymentDeleteWarning");
    }

    if (item.status === "FAILED_PAYMENT") {
      return t("dashboard:menu.failedPaymentDeleteWarning");
    }

    if (item.transactions?.length > 0 && item.transactions[0]?.succeeded_at) {
      return t("dashboard:menu.criticalDeleteWarning");
    }

    return "";
  };

  const closeExportModal = () => {
    setExportModalIsOpen(false);
  }
  const openExportModal = async () => {
    setExportModalIsOpen(true);
  }
  const navigate = useNavigate();

  const handleDownload = async (e) => {
    try {
      e.preventDefault();

      const exportFilters = { ...filters };
      delete exportFilters.page;
      delete exportFilters.per_page;

      const selectedBookingIds = bookings.map((booking) => booking.id).join(',');
      const isManagerHotel = auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER')

      // const response = await BookingService.export({ id: `in:${selectedBookingIds}` }, api);
      let response;
      if (userRoles?.some(role => role.name === "ROLE_HOTEL_MANAGER")) {
        response = await BookingService.export({ ...exportFilters, hotel_id: auth.user?.hotel[0]?.id },
          api
        );

      } else {
        response = await BookingService.export(exportFilters, api);
      }

      let csvData = response;

      const parsed = Papa.parse(csvData, { header: false });

      let filteredData = parsed.data;
      if (isManagerHotel) {
        filteredData = parsed.data.map((row) => {
          if (!Array.isArray(row) || row.length === 0) return row;
          return row.filter((_, index) => ![2, 3, 26, 28, 29].includes(index));
        });
      }

      filteredData = filteredData.map((row) => {
        if (!Array.isArray(row) || row.length === 0) return row;
        if (row.length > 1) {
          let beforelast = row[row.length - 2];
          const phoneValue = (beforelast || '').trim();
          if (phoneValue) {
            row[row.length - 2] = `\t${phoneValue}`;
          }
        }
        return row;
      });

      filteredData = filteredData.map((row) => {
        if (!Array.isArray(row) || row.length === 0) return row;
        return row.map((cell) => {
          if (typeof cell === 'string') {
            return cell.replace(/[\r\n]+/g, ' ').trim();
          }
          return cell;
        });
      });

      csvData = Papa.unparse(filteredData);
      const csvBlob = new Blob([csvData], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(csvBlob);
      link.download = 'client_bookings.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();

      setExportModalIsOpen(false);

    } catch (error) {
      console.error('Error exporting client bookings:', error);
    }
  };
  const [expandedRows, setExpandedRows] = useState({});
  const CHUNK_SIZE = 50;

  const toggleRow = (id, fullLength) => {
    setExpandedRows((prev) => {
      const currentLength = prev[id] || CHUNK_SIZE;
      const isFullyExpanded = currentLength >= fullLength;

      return {
        ...prev,
        [id]: isFullyExpanded ? CHUNK_SIZE : currentLength + CHUNK_SIZE,
      };
    });
  };
  const handleDeleteConfirmation = () => {
    handleDelete(selectedItemId);
    setOpenDeleteDialog(false);
    setDeleteWarning("");
  };

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteWarning("");
  };


  const handleChangeFiche = (e) => {
    setFicheFormData({
      ...ficheFormData,
      [e.target.name]: e.target.value,
    });
  };


  const [showPDF, setShowPDF] = useState(false);

  const handleClickPDF = async (booking) => {
    setShowPDF(true);

    const blob = await BookingService.downloadReceipt(booking.id, api);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${t('dashboard:menu.facture')} ${booking.id}.pdf`;
    link.click();
  };


  const [detailsModalIsOpenFicheHotel, setDetailsModalIsOpenFicheHotel] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [allHotels, setAllHotels] = useState([]);
  const [hotels, setHotels] = useState([]);


  const [ficheFormDataHotel, setFicheFormDataHotel] = useState({
    id: '',
    hotel_id: '',
    tenue_vestimentaire: '',
    parking: '',
    ou_se_diriger: '',
    comment_et_ou_sinstaller: '',
    materiel_a_prendre: '',
    materiel_sur_place: '',
    prestations_que_lon_peut_fournir: '',
    infos_complementaires: '',
  });

  const handleChangeFicheHotel = async (e) => {

    setFicheFormDataHotel({
      ...ficheFormDataHotel,
      [e.target.name]: e.target.value,
    });

  };

  const handleSubmitFicheHotel = async (e) => {
    e.preventDefault();

    try {
      const response = await (ficheFormDataHotel.id ? FicheHotelService.update(ficheFormDataHotel.id, { ...ficheFormDataHotel, hotel_id: selectedHotel.hotel.id }, api) : FicheHotelService.store({ ...ficheFormDataHotel, hotel_id: selectedHotel.hotel.id }, api));
      if (response.data) {
        closeFichesModalHotel();
        fetchBookings();
      }
    } catch (e) {
      // errors handled in interceptor
    }
  };


  const closeFichesModalHotel = () => {
    setSelectedHotel(null);
    setFicheFormDataHotel({
      id: '',
      hotel_id: '',
      tenue_vestimentaire: '',
      parking: '',
      ou_se_diriger: '',
      comment_et_ou_sinstaller: '',
      materiel_a_prendre: '',
      materiel_sur_place: '',
      prestations_que_lon_peut_fournir: '',
      infos_complementaires: '',
    });
    setDetailsModalIsOpenFicheHotel(false);
  };

  const fetchHotels = async () => {
    try {
      const response = await HotelService.index(filters, api);
      console.log("API Response:", response);
      setAllHotels(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...filters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
      setHotels(response.data);
    } catch (error) {

    }
  };

  const openFicheModalHotel = (booking) => {
    setSelectedHotel(booking);
    setFicheFormDataHotel({
      id: booking.hotel.fiche_hotel[0]?.id,
      hotel_id: booking.hotel.fiche_hotel[0]?.hotel_id,
      tenue_vestimentaire: booking.hotel.fiche_hotel[0]?.tenue_vestimentaire,
      parking: booking.hotel.fiche_hotel[0]?.parking,
      ou_se_diriger: booking.hotel.fiche_hotel[0]?.ou_se_diriger,
      comment_et_ou_sinstaller: booking.hotel.fiche_hotel[0]?.comment_et_ou_sinstaller,
      materiel_a_prendre: booking.hotel.fiche_hotel[0]?.materiel_a_prendre,
      materiel_sur_place: booking.hotel.fiche_hotel[0]?.materiel_sur_place,
      prestations_que_lon_peut_fournir: booking.hotel.fiche_hotel[0]?.prestations_que_lon_peut_fournir,
      infos_complementaires: booking.hotel.fiche_hotel[0]?.infos_complementaires,
    });
    setDetailsModalIsOpenFicheHotel(true);
  };


  const handleSubmitFiche = async (e) => {
    e.preventDefault();

    try {
      // const response = await FicheClientService.store({ ...ficheFormData, user_id: selectedCustomer.id }, api);
      const response = await (ficheFormData.id ? FicheClientService.update(ficheFormData.id, { ...ficheFormData, user_id: selectedBooking.client.id }, api) : FicheClientService.store({ ...ficheFormData, user_id: selectedBooking.client.id }, api));
      if (response.data) {
        closeFichesModal();
        fetchBookings();
      }
    } catch (e) {
      // errors handled in interceptor
    }
  };

  const openFicheModal = (booking) => {
    setSelectedBooking(booking);
    setFicheFormData(
      {
        id: booking.client.fiche_client[0]?.id,
        user_id: booking.client.fiche_client[0]?.user_id,
        parking: booking.client.fiche_client[0]?.parking,
        maison_ou_immeuble: booking.client.fiche_client[0]?.maison_ou_immeuble,
        infos_logistiques: booking.client.fiche_client[0]?.infos_logistiques,
        tensions_particulieres: booking.client.fiche_client[0]?.tensions_particulieres,
        pression_ideale: booking.client.fiche_client[0]?.pression_ideale,
        huile_preferee: booking.client.fiche_client[0]?.huile_preferee,
        type_musique: booking.client.fiche_client[0]?.type_musique,
        problemes_physique: booking.client.fiche_client[0]?.problemes_physique,
        parties_preferees: booking.client.fiche_client[0]?.parties_preferees,
        parties_a_ne_pas_masser: booking.client.fiche_client[0]?.parties_a_ne_pas_masser,
        se_fait_masser_nu: booking.client.fiche_client[0]?.se_fait_masser_nu,
        mauvaise_hygiene: booking.client.fiche_client[0]?.mauvaise_hygiene,
        remarque_deplacee: booking.client.fiche_client[0]?.remarque_deplacee,
        age: booking.client.fiche_client[0]?.age,
        type_profession: booking.client.fiche_client[0]?.type_profession,
        source: booking.client.fiche_client[0]?.source,
        classe_sociale: booking.client.fiche_client[0]?.classe_sociale,
        parking_on: booking.client.fiche_client[0]?.parking_on,
        infos_logistiques_on: booking.client.fiche_client[0]?.infos_logistiques_on,
        maison_ou_immeuble_on: booking.client.fiche_client[0]?.maison_ou_immeuble_on,
        problemes_physique_on: booking.client.fiche_client[0]?.problemes_physique_on,
        couleur_on: booking.client.fiche_client[0]?.couleur_on,
        forme_on: booking.client.fiche_client[0]?.forme_on,
        cuticules_on: booking.client.fiche_client[0]?.cuticules_on,
        ongle_on: booking.client.fiche_client[0]?.ongle_on,
        age_on: booking.client.fiche_client[0]?.age_on,
        type_on: booking.client.fiche_client[0]?.type_on,
        entendu_on: booking.client.fiche_client[0]?.entendu_on,
        classe_on: booking.client.fiche_client[0]?.classe_on,
        info_on: booking.client.fiche_client[0]?.info_on,
        info_complementaires: booking.client.fiche_client[0]?.info_complementaires,
      }
    )
    setDetailsModalIsOpenFiche(true);

  };

  const closeFichesModal = () => {
    setSelectedBooking(null);
    setFicheFormData(
      {
        id: '',
        user_id: '',
        parking: '',
        maison_ou_immeuble: '',
        infos_logistiques: '',
        tensions_particulieres: '',
        pression_ideale: '',
        huile_preferee: '',
        type_musique: '',
        problemes_physique: '',
        parties_preferees: '',
        parties_a_ne_pas_masser: '',
        se_fait_masser_nu: '',
        mauvaise_hygiene: '',
        remarque_deplacee: '',
        age: '',
        type_profession: '',
        source: '',
        classe_sociale: '',
        parking_on: '',
        infos_logistiques_on: '',
        maison_ou_immeuble_on: '',
        problemes_physique_on: '',
        couleur_on: '',
        forme_on: '',
        cuticules_on: '',
        ongle_on: '',
        age_on: '',
        type_on: '',
        entendu_on: '',
        classe_on: '',
        info_on: '',
        info_complementaires: '',
      }
    )
    setDetailsModalIsOpenFiche(false);
  };


  const handleClick = (event, index) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
    setAnchorElStatus(null);
  };

  const handleClickStatus = (event, index) => {
    event.stopPropagation();
    setAnchorElStatus(event.currentTarget);
    setSelectedIndex(index);
  };

  const handleStatusChange = async () => {
    try {
      console.log({ pendingStatusChange })
      await BookingService.update(pendingStatusChange.item.id, { status: pendingStatusChange.newStatus, justification: pendingStatusChange.justification }, api);
      notify.success(t('booking:updateSuccess'));
      fetchBookings();
      handleClose();
      setConfirmDialogOpen(false);
      setPendingStatusChange({ item: null, newStatus: null, justification: null });
    } catch (err) {
      console.error(err);

    }
  }

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorElStatus(null);
    setSelectedIndex(null);
  };

  const addIdToWith = (id) => {
    setFilters((prevFilters) => {
      // Créer une copie des filtres existants
      const updatedFilters = { ...prevFilters };


      updatedFilters['id'] = `in:${id}`;

      console.log(updatedFilters);  // Affiche l'objet de filtre mis à jour

      return updatedFilters;
    });
  };

  // Fetch bookings on mount and on filter changes
  const fetchBookings = async () => {
    try {
      const roleBasedFilters = { ...filters };

      if (userRoles?.some(role => role.name === "ROLE_HOTEL_MANAGER")) {
        const response = await BookingService.index({ ...filters, hotel_id: auth.user?.hotel[0]?.id },
          api
        );
        setBookings(response.data);
        console.log(response.data)
        setTotal(response.total);
        setLastPage(response.last_page);

        if (response.last_page < currentPage) {
          setFilters({ ...filters, page: response.last_page });
          setCurrentPage(response.last_page);
        }
      } else {

        const response = await BookingService.index(roleBasedFilters, api);
        console.log("new", roleBasedFilters)
        console.log("old", filters)
        setBookings(response.data);
        console.log("titi", response.data);
        setTotal(response.total);
        setLastPage(response.last_page);

        if (response.last_page < currentPage) {
          setFilters({ ...filters, page: response.last_page });
          setCurrentPage(response.last_page);
        }
      }

    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const scheduleBookingsRefresh = useCallback(() => {
    if (!bookingsRefreshRef.current) {
      return;
    }

    if (refreshInFlightRef.current) {
      return;
    }

    const now = Date.now();
    if (now - lastRefreshRef.current < 1500) {
      return;
    }

    lastRefreshRef.current = now;
    refreshInFlightRef.current = true;

    Promise.resolve(bookingsRefreshRef.current())
      .catch((error) => {
        console.error("Realtime booking refresh failed:", error);
      })
      .finally(() => {
        refreshInFlightRef.current = false;
      });
  }, []);

  useEffect(() => {
    bookingsRefreshRef.current = fetchBookings;
  }, [fetchBookings, filters, currentPage, perPage, userRoles, auth?.user?.id]);

  const fetchFlys = async () => {
    try {

      const response = await UserService.indexByRole({
        status: 'ACTIVE',
        ...(!isPrivileged && { flypro_manager_id: auth?.user?.id }),
      }, 'ROLE_FLYPRO', api);

      const sorted = response.sort((a, b) =>
        a.firstname.localeCompare(b.firstname, 'en', { sensitivity: 'base' })
      );

      setFlys(sorted);

    } catch (error) {
    }
  };

  useEffect(() => {

    fetchFlys();

  }, []);

  useEffect(() => {

    fetchBookings();

  }, [currentPage, perPage, filters, userRoles]);

  useEffect(() => {
    if (!auth?.accessToken) return;

    initPusher({ token: auth?.accessToken });

    const handler = (data) => {
      const payload =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch (_) {
                return {};
              }
            })()
          : data || {};

      if (isHotelManager) {
        const hotelId = auth?.user?.hotel?.[0]?.id;
        if (hotelId && payload?.hotel_id && Number(payload.hotel_id) !== Number(hotelId)) {
          return;
        }
      }

      scheduleBookingsRefresh();
    };

    const publicHandler = (data) => {
      const envelope =
        typeof data === "string"
          ? (() => {
              try {
                return JSON.parse(data);
              } catch (_) {
                return {};
              }
            })()
          : data || {};

      if (envelope?.key !== "dashboard.bookings") {
        return;
      }

      const payload = envelope?.payload || {};

      if (isHotelManager) {
        const hotelId = auth?.user?.hotel?.[0]?.id;
        if (
          hotelId &&
          payload?.hotel_id &&
          Number(payload.hotel_id) !== Number(hotelId)
        ) {
          return;
        }
      }

      scheduleBookingsRefresh();
    };

    const unbindPrivate = bindEvent(
      "private-admin-dashboard",
      "dashboard.bookings.updated",
      handler
    );

    const unbindPublic = bindEvent(
      "public-data",
      "realtime.data.updated",
      publicHandler
    );

    return () => {
      unbindPrivate();
      unbindPublic();
    };
  }, [auth?.accessToken, auth?.user?.hotel, isHotelManager, scheduleBookingsRefresh]);

  useEffect(() => {
    if (!auth?.accessToken) return;

    initPusher({ token: auth?.accessToken });
    const pusher = getPusher();
    if (!pusher) return;

    const handleConnected = () => {
      scheduleBookingsRefresh();
    };

    pusher.connection.bind("connected", handleConnected);

    return () => {
      pusher.connection.unbind("connected", handleConnected);
    };
  }, [auth?.accessToken, scheduleBookingsRefresh]);

  useEffect(() => {
    const handleOnline = () => {
      scheduleBookingsRefresh();
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [scheduleBookingsRefresh]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "search_by") {
      setFilterData(f => ({
        ...f,
        search_by: value,
        search: "",
      }));

      const search = document.querySelector('input[name="search"]');
      search.placeholder = `${t('dashboard:table.searchBy')} ${e.target.options[e.target.selectedIndex].innerHTML}`;
      search.type = (value === "date" || value === "start_before") ? "date" : "text";

      if (value === "date_range") {
        setDateRange({ start_date: "", end_date: "" });
      }

      return;
    }

    if (name === "start_date" || name === "end_date") {
      setDateRange(d => ({ ...d, [name]: value }));
      return;
    }

    if (name === "status") {
      setFilterData(f => ({ ...f, status: value }));

      setFilters(f => {
        const newFilters = { ...f, page: 1 };

        if (value) {
          newFilters.status = value;
        } else {
          delete newFilters.status;
        }

        return newFilters;
      });

      return;
    }

    if (name === "flypro") {
      setFilterData(f => ({ ...f, flypro: value }));

      setFilters(f => {
        const newFilters = { ...f, page: 1 };

        if (value) {
          newFilters['flypro_id'] = value;
        } else {
          delete newFilters['flypro_id'];
        }

        return newFilters;
      });

      return;
    }


    if (name === "search" && value === "") {
      setFilterData(f => ({ ...f, search: "" }));

      setFilters(f => {
        // const newFilters = { page: 1 };
        const BASE_FILTERS = {
          with: `ratings,address,client.roles,client.ficheClient,products.category,booking_products,creator,editor,therapists,therapists.media,id,transactions,hotel,hotel.ficheHotel,hotel.address,histories,histories.editor,histories.creator`,
          page: currentPage,
          per_page: perPage,
          sort: '-created_at',
        };

        let newFilters = { ...BASE_FILTERS, page: 1 };
        if (filterData.status) newFilters.status = filterData.status;
        return newFilters;
      });

      return;
    }

    setFilterData(f => ({ ...f, [name]: value }));
  };



  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const BASE_FILTERS = {
      with: `ratings,address,client.roles,client.ficheClient,products.category,booking_products,creator,editor,therapists,therapists.media,id,transactions,hotel,hotel.ficheHotel,hotel.address,histories,histories.editor,histories.creator`,
      page: currentPage,
      per_page: perPage,
      sort: '-created_at',
    };

    // let newFilters = { ...filters, page: 1};
    let newFilters = { ...BASE_FILTERS, page: 1 };

    if ((!filterData.search || filterData.search.trim() === "") && filterData.search_by !== "date_range") {
      setFilters(newFilters);
      return;
    }

    if (id) {
      newFilters.id = `in:${id}`;
    }

    if (filterData.status) {
      newFilters.status = filterData.status;
    }

    if (filterData.flypro) {
      newFilters['flypro_id'] = filterData.flypro;
    }

    if (filterData.search_by === "date_range" && dateRange.start_date && dateRange.end_date) {
      newFilters.start_before = `between:${dateRange.start_date},${dateRange.end_date}T23:59:59`;
    }

    else if ((filterData.search_by === "date" || filterData.search_by === "start_before")
      && filterData.search) {
      newFilters.start_before = `${filterData.operator}:${filterData.search}`;
    }

    else if (filterData.search) {
      const map = {
        "client.extra.full_name": "client.fullname",
        "client.email": "client.email",
        "hotel": "hotel.name",
        "phone": "phone",
      };

      const key = map[filterData.search_by] || filterData.search_by;
      newFilters[key] = `${filterData.operator}:${filterData.search}`;
    }

    setFilters(newFilters);
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const handleQuickDateRange = (e) => {
    const { value } = e.target;
    if (!value) {
      setDateRange({ start_date: "", end_date: "" });
      // delete newFilters['start_before'];
      fetchBookings();
      return;
    }

    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (value) {
      case 'today':
        startDate = today;
        endDate = today;
        break;

      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(startDate);
        break;

      case 'this_week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay() + 1);
        endDate = new Date(today);
        endDate.setDate(today.getDate() + (7 - today.getDay()));
        break;

      case 'last_week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay() - 6);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - today.getDay());
        break;

      case 'this_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;

      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;

      case 'last_30_days':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 30);
        endDate = today;
        break;

      default:
        return;
    }

    setDateRange({
      start_date: formatDate(startDate),
      end_date: formatDate(endDate)
    });

  };

  const handleNextPage = () => {
    setFilters({ ...filters, page: currentPage + 1 });
    setCurrentPage(currentPage + 1);
  }

  const handlePrevPage = () => {
    setFilters({ ...filters, page: currentPage - 1 });
    setCurrentPage(currentPage - 1);
  }

  const handleLastPage = () => {
    setFilters({ ...filters, page: lastPage });
    setCurrentPage(lastPage);
  }

  const handleFirstPage = () => {
    setFilters({ ...filters, page: 1 });
    setCurrentPage(1);
  }

  const handlePerPageChange = (e) => {
    setFilters({ ...filters, per_page: e.target.value, page: 1 });
    setPerPage(e.target.value);
    setCurrentPage(1);

  }

  const handleSort = async (event) => {
    const selectedColumn = event.currentTarget.id;

    let newSortDirection = true;
    if (sortField === selectedColumn) {
      newSortDirection = !sortDirection; // Toggle direction for the same column
    }

    setSortField(selectedColumn);
    setSortDirection(newSortDirection);

    const sortParam = `${newSortDirection ? '' : '-'}${selectedColumn}`;
    const updatedFilters = { ...filters, sort: sortParam, page: 1 };

    setFilters(updatedFilters);
  };

  const openDetailsModal = (booking) => {
    setSelectedBooking(booking);
    setDetailsModalIsOpen(true);
  };

  const getAdminPromoCode = (booking) => {
    const value = booking?.admin_promocode || booking?.raw_promocode || booking?.promocode || "";
    return typeof value === "string" ? value.trim() : "";
  };

  const getSupplementPayment = (booking) => booking?.latest_supplement_payment || null;

  const formatMoney = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "-";

    const formatted = Number.isInteger(number)
      ? number.toString()
      : number.toFixed(2);

    return `${formatted} CHF`;
  };

  const getProductsGrossTotal = (booking) => {
    return booking?.products?.reduce((total, product) => total + Number(product.price || 0), 0) || 0;
  };

  const getBookingNetTotal = (booking) => {
    const totalFare = Number(booking?.total_fare);
    if (Number.isFinite(totalFare) && totalFare > 0) return totalFare;

    const subtotal = Number(booking?.subtotal);
    if (Number.isFinite(subtotal)) return subtotal;

    return getProductsGrossTotal(booking);
  };

  const getPromoDiscountAmount = (booking) => {
    if (!getAdminPromoCode(booking)) return 0;

    const discount = getProductsGrossTotal(booking) - getBookingNetTotal(booking);
    return discount > 0 ? discount : 0;
  };

  const getBookingTotalSummary = (booking) => {
    const grossTotal = getProductsGrossTotal(booking);
    const netTotal = getBookingNetTotal(booking);
    const promoCode = getAdminPromoCode(booking);
    const promoDiscount = getPromoDiscountAmount(booking);

    const parts = [`Total produits: ${formatMoney(grossTotal)}`];

    if (promoCode && promoDiscount > 0) {
      parts.push(`Remise ${promoCode}: -${formatMoney(promoDiscount)}`);
    }

    parts.push(`Total TTC: ${formatMoney(netTotal)}`);

    return parts.join(" - ");
  };

  const getSupplementBadgeConfig = (supplement) => {
    if (!supplement) return null;

    const base = {
      display: "inline-block",
      marginTop: 4,
      padding: "3px 7px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
    };

    if (supplement.status === "PAID") {
      return { label: "Supplément payé", style: { ...base, background: "#E8F7E8", color: "#269B2E" } };
    }

    if (supplement.status === "FAILED") {
      return { label: "Paiement supplément échoué", style: { ...base, background: "#FDECEC", color: "#D83933" } };
    }

    if (supplement.status === "MANUAL_INVOICE_REQUIRED") {
      const label = supplement.is_same_day
        ? "Urgent: à facturer séparément"
        : "À facturer séparément";
      return { label, style: { ...base, background: "#FFF4DC", color: "#A76900" } };
    }

    const label = supplement.is_same_day
      ? "Urgent: supplément à suivre"
      : "Supplément en attente";
    return { label, style: { ...base, background: "#FFF4DC", color: "#A76900" } };
  };

  const SupplementBadge = ({ booking }) => {
    const config = getSupplementBadgeConfig(getSupplementPayment(booking));
    if (!config) return null;

    return <div style={config.style}>{config.label}</div>;
  };

  const SupplementDetails = ({ booking }) => {
    const supplement = getSupplementPayment(booking);
    if (!supplement) return null;

    const badgeConfig = getSupplementBadgeConfig(supplement);
    const supplementLabelByStatus = {
      PAID: "Supplément payé",
      FAILED: "Paiement supplément échoué",
      PENDING_PAYMENT: "Supplément en attente",
      MANUAL_INVOICE_REQUIRED: "Supplément à facturer",
    };
    const totalLabel = ["FAILED", "PENDING_PAYMENT"].includes(supplement.status)
      ? "Total demandé si payé"
      : "Total TTC";
    const details = [
      `Déjà payé: ${formatMoney(supplement.old_paid_amount)}`,
      `${supplementLabelByStatus[supplement.status] || "Supplément"}: ${formatMoney(supplement.added_amount)}`,
      `${totalLabel}: ${formatMoney(supplement.new_net_total)}`,
    ];

    return (
      <div
        className="input-hotel-container contain-marge"
        style={{
          display: "block",
          marginTop: 16,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 8,
            padding: "0 18px",
          }}
        >
          <label
            className="input-hotel-label"
            style={{
              display: "block",
              margin: 0,
              paddingLeft: 0,
              position: "static",
              transform: "none",
            }}
          >
            Supplément:
          </label>
          {badgeConfig && (
            <span
              style={{
                ...badgeConfig.style,
                marginTop: 0,
                maxWidth: "100%",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              {badgeConfig.label}
            </span>
          )}
        </div>
        <input
          type="text"
          name="supplement_payment"
          value={details.join(" - ")}
          readOnly
          style={{
            height: 50,
            lineHeight: "50px",
            fontSize: 14,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
      </div>
    );
  };


  const cancelBooking = async (booking) => {
    setSelectedBooking(booking);
    console.log('booking item cancel', booking)


    try {
      const response = await BookingService.cancel(booking.id, api);
      console.log({ response })
      fetchBookings();
      const refundType = response.data?.refundType;

      if (refundType === 'FULL_REFUND') {
        // notify.success(t('full_refund_message'));
        setCancelSuccessMessage(t('cancel_message_f'));
      } else if (refundType === 'CREDIT_NOTE') {
        //notify.info(t('credit_note_message', { code: response.data.promoCode }));
        setCancelSuccessMessage(t('cancel_message_f'));
      } else if (refundType === 'NO_REFUND') {
        //notify.warning(t('no_refund_message'));
        setCancelSuccessMessage(t('cancel_message_f'));
      }
      setShowCancelConfirm(true);



    } catch (error) {

    }
  };

  const sendPayLink = async (booking) => {
    try {
      const response = await BookingService.resendPaymentLink(booking.id, api);
      notify.success(response?.message || t('dashboard:link'));
    } catch (error) {
      notify.error(error?.response?.data?.message || "Impossible de renvoyer le lien de paiement.");
    }
  };

  const duplicateBooking = (booking) => {
    setBookingToDuplicate(booking);
  };

  const closeDetailsModal = () => {
    setSelectedBooking(null);
    setDetailsModalIsOpen(false);
  };

  const handleEditMouseEnter = (index) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredRow(index);
  };

  const handleEditMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setHoveredRow(null);
    }, 250);
  };

  const handleDelete = async (id) => {
    try {
      const response = await BookingService.cancelDelete(id, api);
      fetchBookings();
    } catch (error) {

    }
  };

  const [activeTab, setActiveTab] = useState('form');

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const filterMenuOpen = Boolean(filterMenuAnchor);

  const handleFilterMenuClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const isLessThan24h = (bookingDate) => {
    const now = new Date();
    const booking = new Date(bookingDate);
    const diffHours = (booking - now) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const handleCancelBooking = async (option) => {
    if (!option) option = 'FULL_REFUND';

    try {
      const response = await BookingService.cancel(
        cancelBookingId,
        api,
        { params: { option } }
      );
      await fetchBookings();
      setShowCancelConfirm(false);

      const { refundType, promoCode } = response.data || {};
      if (refundType === 'FULL_REFUND') {
        setCancelSuccessMessage(t('cancel_message_f'));
      } else if (refundType === 'FLATT_OFF') {
        setCancelSuccessMessage(t('cancel_message_f'))
      } else {
        setCancelSuccessMessage(t('cancel_message_f'))
      }

      setShowCancelConfirm(true);

    } catch (err) {

    }
  };

  const showCancelModal = (booking) => {
    let message = "";
    if (booking?.offline_payment) {
      message = t('dashboard:confirm_cancel_offline');
      setCancelChoicePhase(false);
    } else {
      const now = new Date();
      const start = new Date(booking.start_before);
      const diffInHours = (start - now) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        message = t('dashboard:cancel_w_24');
        setCancelChoicePhase(false);
      } else if (diffInHours >= 24 && diffInHours <= 120) {
        message = t('dashboard:cancel_promo_notice');
        setCancelChoicePhase(true);
      } else {
        message = t('dashboard:cancel_refund_notice');
        setCancelChoicePhase(false);
      }
    }

    setCancelBookingId(booking.id);
    setCancelMessage(message);
    setCancelSuccessMessage("");
    setShowCancelConfirm(true);
  };

  const renderAdminFlexibleCancelMenuItem = (booking) => {
    if (!canAdminFlexibleCancel(booking)) {
      return null;
    }

    return (
      <MenuItem
        style={{ color: "#9a5a00" }}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
          openAdminFlexibleCancellation(booking);
        }}
      >
        <ListItemIcon>
          <CancelIcon style={{ color: "#9a5a00" }} />
        </ListItemIcon>
        <ListItemText>{t('dashboard:adminCancel.flexibleAction')}</ListItemText>
      </MenuItem>
    );
  };

  const renderClientHistoryMenuItem = (booking) => {
    const clientId = booking?.client?.id || booking?.client_id;
    if (!clientId || !booking?.client?.roles?.some(role => role.name === 'ROLE_CLIENT')) return null;

    return (
      <MenuItem
        onClick={(event) => {
          event.stopPropagation();
          handleClose();
          navigate(`/customer?id=${clientId}&history=1`);
        }}
      >
        <ListItemIcon>
          <HistoryIcon style={{ color: "#1976d2" }} />
        </ListItemIcon>
        <ListItemText>{t('dashboard:menu.bookingHistory')}</ListItemText>
      </MenuItem>
    );
  };

  const openProlongModal = async (booking) => {
    setIsProlongLoading(true);
    setProlongErrorMessage('');
    setProlongationErrorMessage('');
    setSelectedProlongProduct(null);

    try {
      // Calculate current duration

      console.log("booking :  ", booking);
      const currentDuration = booking.products.reduce((sum, p) => sum + (p.duration || 0), 0);

      console.log("Current duration for booking:", currentDuration);

      // Fetch available products for this category
      const productsResp = await ProductService.index({
        category_id: booking.products[0].category_id,
        active: 1
      }, api);

      console.log("All products for prolongation:", productsResp);

      const sortedProducts = [...productsResp].sort((a, b) => a.duration - b.duration);
      const filteredProducts = sortedProducts
        .map(p => ({
          ...p,
          _diffDuration: p.duration - currentDuration
        }))
        .filter(p => p._diffDuration > 0);

      console.log("Filtered Products:", filteredProducts);

      setAvailableProlongProducts(filteredProducts);
      setBookingToProlong(booking);

      // For time display
      const bookingEndTime = getExtendedEndTime(booking.start_before, currentDuration);
      setExtendedEndTime(null);
      setOriginalEndTime(bookingEndTime);
      setShowProlongModal(true);
    } catch (error) {
      setProlongErrorMessage("Erreur lors du chargement des produits.");
    } finally {
      setIsProlongLoading(false);
    }
  };


  const handleProlongation = async () => {
    if (!bookingToProlong) return;
    if (!selectedProlongProduct) {
      setProlongationErrorMessage("Veuillez choisir une prolongation.");
      return;
    }
    setIsProlongLoading(true);
    setProlongErrorMessage('');
    setProlongationErrorMessage("");

    try {
      const oldProduct = bookingToProlong.products[0];
      const oldDuration = Number(oldProduct.duration);
      const diff = parseInt(selectedProlongProduct._diffDuration, 10);
      const newTargetDuration = oldDuration + diff;

      // Fetch products again for safety
      const productsResp = await ProductService.index({
        category_id: oldProduct.category_id,
        active: 1,
      }, api);

      const newProduct = productsResp.find(p => Number(p.duration) === newTargetDuration);

      if (!newProduct) {
        setProlongErrorMessage("Aucun produit trouvé pour cette durée.");
        setIsProlongLoading(false);
        return;
      }

      const updatedPayload = {
        products: [newProduct.id],
        duration: parseInt(newProduct.duration, 10),
      };

      await BookingService.update(
        bookingToProlong.id,
        updatedPayload,
        api
      );

      setShowProlongModal(false);
      setBookingToProlong(null);
      setSelectedProlongProduct(null);
      setProlongErrorMessage('');
      setProlongationErrorMessage("");

      // Refresh your table!
      fetchBookings();

      notify.success("Prolongation appliquée avec succès !");
    } catch (err) {
      setProlongErrorMessage("Erreur lors de la prolongation.");
    } finally {
      setIsProlongLoading(false);
    }
  };




  return (
    <>
      {showHeader && <MenuHeader />}

      <div className="bloc-search background-breadcrumb bloc-search-mobile  bloc-dashboard">
        <div className="container">
          {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
            <div className="search">
              <div className="breadcrumb-navigation">
                <button
                  className={`breadcrumb-map-button ${selected === "list" ? "active" : ""}`}
                  onClick={() => handleButtonClick("list")}
                >
                  <div className={`icon ${selected === "list" ? "active-icon" : ""}`} />
                  <span className="breadcrumb-label">{t('list')}</span>
                </button>

                <button
                  className={`breadcrumb-list-button ${selected === "map" ? "active" : ""}`}
                  onClick={() => handleButtonClick("map")}
                >
                  <div className={`icon ${selected === "map" ? "active-icon" : ""}`} />
                  <span className="breadcrumb-label-list">{t('map')}</span>
                </button>
              </div>
            </div>
          )}
          <div className="desktop-hidden">
            <IconButton onClick={handleFilterMenuClick}>
              <img src='assets/images/filter.png' alt='actions' height={20} />
            </IconButton>
            <Menu anchorEl={filterMenuAnchor}
              open={filterMenuOpen}
              onClose={handleFilterMenuClose}
            >
              <MenuList
                style={{ padding: 0 }}
              >
                <MenuItem onClick={handleFilterMenuClose} disableRipple>
                  <ListItemIcon>
                    <img src='assets/images/filter.png' alt='actions' height={20} />

                  </ListItemIcon>
                  <ListItemText>Filters</ListItemText>
                </MenuItem>
                <Divider></Divider>
                <form onSubmit={handleFilterSubmit} className="search">
                  <div className="bloc-filtre popu-dash">
                    {filterData.search_by === "date_range" && (
                      <select
                        name="quick_date_range"
                        onChange={handleQuickDateRange}
                        className="form-text"
                      >
                        <option value="">{t('search.dateRange.custom')}</option>
                        <option value="today">{t('search.dateRange.today')}</option>
                        <option value="yesterday">{t('search.dateRange.yesterday')}</option>
                        <option value="this_week">{t('search.dateRange.thisWeek')}</option>
                        <option value="last_week">{t('search.dateRange.lastWeek')}</option>
                        <option value="this_month">{t('search.dateRange.thisMonth')}</option>
                        <option value="last_month">{t('search.dateRange.lastMonth')}</option>
                        <option value="last_30_days">{t('search.dateRange.last30Days')}</option>
                      </select>
                    )}
                    <select
                      name="search_by"
                      value={filterData.search_by}
                      onChange={handleFilterChange}
                    >
                      <option value="id">{t('search.by.taskId')}</option>
                      <option value="start_before">{t('search.by.start_before')}</option>
                      <option value="date">{t('search.by.date')}</option>
                      <option value="date_range">{t('search.by.date_range')}</option>
                      <option value="duration">{t('search.by.duration')}</option>
                      <option value="phone">{t('search.by.phone')}</option>
                      <option value="hotel">{t('search.by.hotel')}</option>
                      <option value="client.extra.full_name">{t('search.by.client')}</option>
                      <option value="client.email">{t('search.by.emailClient')}</option>


                    </select>

                    <select
                      name="operator"
                      value={filterData.operator}
                      onChange={handleFilterChange}
                    >
                      <option value="contains">{t('search.operator.contains')}</option>
                      <option value="starts">{t('search.operator.starts')}</option>
                      <option value="ends">{t('search.operator.ends')}</option>
                      <option value="eq">{t('search.operator.eq')}</option>
                      <option value="!eq">{t('search.operator.notEq')}</option>
                      <option value="in">{t('search.operator.in')}</option>
                    </select>
                    {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                      <select
                        name="status"
                        value={filterData.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('search.status.all')}</option>
                        <option value="CREATED">{t('search.status.created')}</option>
                        <option value="ACKNOWLEDGED">{t('search.status.acknowledged')}</option>
                        <option value="PENDING_ACKNOWLEDGEMENT">{t('search.status.pendingAcknowledgement')}</option>
                        <option value="DECLINED">{t('search.status.declined')}</option>
                        <option value="IN_TRANSIT">{t('status.IN_TRANSIT')}</option>
                        <option value="REACHED">{t('search.status.reached')}</option>
                        <option value="COMPLETED">{t('search.status.completed')}</option>
                        <option value="CANCELED">{t('status.CANCELED')}</option>
                        <option value="FAILED">{t('search.status.failed')}</option>
                      </select>
                    ) : (
                      <select
                        name="status"
                        value={filterData.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('search.status.all')}</option>
                        <option value="PENDING_ACKNOWLEDGEMENT">{t('statusManager.PENDING_ACKNOWLEDGEMENT')}</option>
                        <option value="ACKNOWLEDGED">{t('search.status.acknowledged')}</option>
                        <option value="DECLINED">{t('statusManager.DECLINED')}</option>
                        <option value="IN_TRANSIT">{t('status.IN_TRANSIT')}</option>
                        <option value="REACHED">{t('search.status.reached')}</option>
                        <option value="COMPLETED">{t('search.status.completed')}</option>
                        <option value="CANCELED">{t('status.CANCELED')}</option>
                        <option value="FAILED">{t('search.status.failed')}</option>
                      </select>
                    )}

                    <select
                      name="flypro"
                      value={filterData.flypro}
                      onChange={handleFilterChange}
                    >
                      <option value="">{t('search.flyproAll')}</option>
                      {flypros.map((fly) => (
                        <option value={fly.id}>
                          {fly.firstname}
                        </option>
                      ))}
                    </select>

                    <IconButton className="icon-excel" style={{ width: 40 }} onClick={handleDownload}>
                      <img src='assets/images/icon-excel.svg' alt='actions' />
                    </IconButton>
                  </div>

                </form>

              </MenuList>
            </Menu>
          </div>
          <div className="mobile-hidden" style={{ overflowX: 'auto', overflowY: 'hidden', padding: '0 10px', scrollbarWidth: 'none' }}>
            <form onSubmit={handleFilterSubmit} className="search" style={{ gap: '5px', flexFlow: 'nowrap' }}>

              <div className="text-search" style={{ display: filterData.search_by === "date_range" ? "none" : "" }}>
                <input
                  type="text"
                  name="search"
                  value={filterData.search}
                  onChange={handleFilterChange}
                  placeholder={t('search.placeholder')}
                  className="form-text"
                />
                <input type="submit" className="form-submit" value={t('search.button')} style={{ fontSize: '16px' }} />
              </div>
              {filterData.search_by === "date_range" && (
                <>
                  <div className="text-search">
                    <input
                      type="date"
                      name="start_date"
                      value={dateRange.start_date}
                      onChange={handleFilterChange}
                      className="form-text"
                      style={{ width: 'unset', padding: 0 }}
                    />
                    <input
                      type="date"
                      name="end_date"
                      value={dateRange.end_date}
                      onChange={handleFilterChange}
                      className="form-text"
                      style={{ width: 'unset', padding: 0 }}
                    />
                    <input type="submit" className="form-submit" value={t('search.button')} style={{ fontSize: '16px', padding: '5px' }} />
                  </div>
                </>
              )}
              <div className="bloc-filtre" style={{ gap: '5px' }}>
                {filterData.search_by === "date_range" && (
                  <select
                    name="quick_date_range"
                    onChange={handleQuickDateRange}
                    className="form-text"
                  >
                    <option value="">{t('search.dateRange.custom')}</option>
                    <option value="today">{t('search.dateRange.today')}</option>
                    <option value="yesterday">{t('search.dateRange.yesterday')}</option>
                    <option value="this_week">{t('search.dateRange.thisWeek')}</option>
                    <option value="last_week">{t('search.dateRange.lastWeek')}</option>
                    <option value="this_month">{t('search.dateRange.thisMonth')}</option>
                    <option value="last_month">{t('search.dateRange.lastMonth')}</option>
                    <option value="last_30_days">{t('search.dateRange.last30Days')}</option>
                  </select>
                )}
                <select
                  name="search_by"
                  value={filterData.search_by}
                  onChange={handleFilterChange}
                >
                  <option value="id">{t('search.by.taskId')}</option>
                  <option value="start_before">{t('search.by.start_before')}</option>
                  <option value="date">{t('search.by.date')}</option>
                  <option value="date_range">{t('search.by.date_range')}</option>
                  <option value="duration">{t('search.by.duration')}</option>
                  <option value="phone">{t('search.by.phone')}</option>
                  <option value="hotel">{t('search.by.hotel')}</option>
                  <option value="client.extra.full_name">{t('search.by.client')}</option>
                  <option value="client.email">{t('search.by.emailClient')}</option>


                </select>

                <select
                  name="operator"
                  value={filterData.operator}
                  onChange={handleFilterChange}
                >
                  <option value="contains">{t('search.operator.contains')}</option>
                  <option value="starts">{t('search.operator.starts')}</option>
                  <option value="ends">{t('search.operator.ends')}</option>
                  <option value="eq">{t('search.operator.eq')}</option>
                  <option value="!eq">{t('search.operator.notEq')}</option>
                  <option value="in">{t('search.operator.in')}</option>
                </select>
                {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                  <select
                    name="status"
                    value={filterData.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">{t('search.status.all')}</option>
                    <option value="CREATED">{t('search.status.created')}</option>
                    <option value="ACKNOWLEDGED">{t('search.status.acknowledged')}</option>
                    <option value="PENDING_ACKNOWLEDGEMENT">{t('search.status.pendingAcknowledgement')}</option>
                    <option value="DECLINED">{t('search.status.declined')}</option>
                    <option value="IN_TRANSIT">{t('status.IN_TRANSIT')}</option>
                    <option value="REACHED">{t('search.status.reached')}</option>
                    <option value="COMPLETED">{t('search.status.completed')}</option>
                    <option value="CANCELED">{t('status.CANCELED')}</option>
                    <option value="FAILED">{t('search.status.failed')}</option>
                  </select>
                ) : (
                  <select
                    name="status"
                    value={filterData.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">{t('search.status.all')}</option>
                    <option value="PENDING_ACKNOWLEDGEMENT">{t('statusManager.PENDING_ACKNOWLEDGEMENT')}</option>
                    <option value="ACKNOWLEDGED">{t('search.status.acknowledged')}</option>
                    <option value="DECLINED">{t('statusManager.DECLINED')}</option>
                    <option value="IN_TRANSIT">{t('status.IN_TRANSIT')}</option>
                    <option value="REACHED">{t('search.status.reached')}</option>
                    <option value="COMPLETED">{t('search.status.completed')}</option>
                    <option value="CANCELED">{t('status.CANCELED')}</option>
                    <option value="FAILED">{t('search.status.failed')}</option>
                  </select>
                )}
                <select
                  name="flypro"
                  value={filterData.flypro}
                  onChange={handleFilterChange}
                >
                  <option value="">{t('search.flyproAll')}</option>
                  {flypros.map((fly) => (
                    <option key={fly.id} value={fly.id}>
                      {fly.firstname}
                    </option>
                  ))}
                </select>

                <IconButton className="icon-excel" style={{ width: 40 }} onClick={handleDownload}>
                  <img src='assets/images/icon-excel.svg' alt='actions' />
                </IconButton>
              </div>
            </form>
          </div>
          {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') ? (
            <CreateTaskManagerModal
              api={api}
              onSuccess={() => {
                fetchBookings();
                setBookingToDuplicate(null);
              }}
              bookingToDuplicate={bookingToDuplicate}
              onClose={() => {
                setBookingToDuplicate(null);
              }}
            />
          ) : (
            <CreateTaskModal
              api={api}
              onSuccess={() => {
                fetchBookings();
                setBookingToDuplicate(null);
              }}
              bookingToDuplicate={bookingToDuplicate}
              onClose={() => {
                setBookingToDuplicate(null);
              }}
            />
          )}

        </div>
      </div >

      <div className="tableau-list view-mobile">
        <div className="container">
          {/* --- HOTEL MANAGER BODY --- */}
          {
            auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <div>
                {translatedBookings?.map((item, index) => (
                  <div className="list-items pointer-click" key={index} onClick={() => { openDetailsModal(bookings[index]); }}>
                    <div style={{ fontWeight: "bold" }}>{t('dashboard:table.orderId')}: {item.id}</div>
                    <div>{t('dashboard:table.product')}:<br /> {item.products?.map((product, index) => (
                      <span key={index}>
                        - {product?.category?.title} {product.duration} min
                        <br />
                      </span>
                    ))}
                    </div>
                    <div>{t('dashboard:table.description')}:
                      {(() => {
                        // const descText = item.description ? `- Description: ${item.description}` : '';
                        const commentText = item.message ? `- Comment: ${item.message}` : '';
                        // const jusText = item.justification ? `- Justification: ${item.justification}` : '';
                        const fullText = [commentText].filter(Boolean).join('\n');
                        const isExpanded = (expandedRows[item.id] || CHUNK_SIZE) >= fullText.length;
                        const displayText = fullText.slice(0, expandedRows[item.id] || CHUNK_SIZE);

                        return (
                          <>
                            {displayText.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}
                            {fullText.length > CHUNK_SIZE && (
                              <span
                                className="show-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(item.id, fullText.length);
                                }}
                                style={{
                                  color: "black",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  fontWeight: "bold",
                                }}
                              >
                                {isExpanded ? " Show Less" : " ... Show More"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ whiteSpace: "pre-line" }}>
                      {t('dashboard:table.flyingDetails')}:<br /> {item.therapists
                        ?.map(
                          (therapist) =>
                            `-  ${t('table.name')}: ${therapist.firstname}`
                        )
                        .flatMap((text, index, array) =>
                          index < array.length - 1
                            ? [text, <br key={index} />]
                            : [text]
                        )}
                    </div>
                    {/* <div>{addressFormat(item.address)}</div> */}
                    <div>{t('dashboard:table.createdBy')}: {item.client?.roles?.some((role) => role.name !== "ROLE_CLIENT") ? item.hotel?.name : item.client?.extra.full_name}<br />
                      <HotelClientBadges booking={item} t={t} /></div>
                    <div>{t('dashboard:table.createdAt')}: {castDate(item.created_at).replace(/:\d{2}$/, '')}</div>
                    <div>{t('dashboard:table.startBefore')}: {castDate(item.start_before).replace(/:\d{2}$/, '')}</div>
                    <div>{t('dashboard:table.completeBefore')}: {castDate(
                      new Date(
                        new Date(item.start_before).getTime() + item.duration * 60 * 1000
                      )
                    ).replace(/:\d{2}$/, '')}
                    </div>
                    <div style={{
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>{t('dashboard:table.taskStatus')}: <StatusBadge status={item?.status} namespace="statusManager" style="mobile" />
                    </div>
                    <div className="menu-edit">
                      <IconButton onClick={(event) => { handleClick(event, index) }}>
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedIndex === index}
                        onClose={handleClose}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >

                        <MenuList style={{ minWidth: 150 }}>
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') &&
                            !item.creator?.roles?.some(role => role.name === 'ROLE_CLIENT') &&
                            !item.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && (
                              <>
                                {(() => {
                                  const now = new Date();
                                  const startTime = new Date(item.start_before);
                                  const endTime = new Date(startTime.getTime() + Number(item.products[0].duration) * 60 * 1000);
                                  const diffHours = (startTime - now) / (1000 * 60 * 60);
                                  const diffHoursEnd = (endTime - now) / (1000 * 60 * 60);
                                  // Only show if startTime is in the future
                                  if (endTime > now) {
                                    if (diffHours > 24) {
                                      return (
                                        <MenuItem
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleClose();
                                            navigate(`/bookings/${item.id}/edit`, { state: { bookingToEdit: item } });
                                          }}
                                        >
                                          <ListItemIcon>
                                            <EditIcon style={{ color: "#1976d2" }} />
                                          </ListItemIcon>
                                          <ListItemText>{t('dashboard:edits')}</ListItemText>
                                        </MenuItem>
                                      );
                                    }
                                    if ((diffHours <= 24 || diffHoursEnd <= 24) && item.products?.length === 1) {
                                      return (
                                        <MenuItem onClick={() => { handleClose(); openProlongModal(item); }}>
                                          <ListItemIcon>
                                            <TimerIcon style={{ color: "#1976d2" }} />
                                          </ListItemIcon>
                                          <ListItemText>{t('commande:prolongation.button')}</ListItemText>
                                        </MenuItem>
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              openDetailsModal(item);
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.viewDetails')}</ListItemText>
                          </MenuItem>
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') &&
                            !item.creator?.roles?.some(role => role.name === 'ROLE_CLIENT') &&
                            !item.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && (
	                              <>
	                                {new Date(item.start_before) > new Date() && (
	                                  <MenuItem
                                    style={{ color: "red" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClose();
                                      setSelectedItem(item);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CancelIcon style={{ color: "red" }} />
                                    </ListItemIcon>
	                                    <ListItemText>{t('dashboard:cancel')}</ListItemText>
	                                  </MenuItem>
	                                )}
		                                {renderClientHistoryMenuItem(item)}
		                                {renderAdminFlexibleCancelMenuItem(item)}
	                              </>
	                            )}

	                        </MenuList>
                      </Menu>
                    </div>
                  </div>
                ))}
              </div>
            )
          }

          {/* --- ADMIN BODY --- */}
          {
            auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
              <div>
                {translatedBookings?.map((item, index) => (
                  <div className="list-items pointer-click" key={index} onClick={() => { openDetailsModal(item); }}>
                    <div style={{ fontWeight: "bold" }}>ID: {item.id}</div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>{t('dashboard:adminTable.status')}: <StatusBadge
                        status={
                          hasMixedPaidBookingProducts(item)
                            ? 'PENDING_PROLONGATION_PAYMENT'
                            : item?.status
                        } style="mobile"
                      />
                    </div>
                    {getAdminPromoCode(item) && (
                      <div >Promo: {getAdminPromoCode(item)}</div>
                    )}
                    <SupplementBadge booking={item} />
                    <div>{t('dashboard:adminTable.flypro')}:
                      {item.therapists?.map((therapist) => (
                        <div key={therapist.id} className={styles.flyproEntry}>
                          <div>{getFlyproDisplayName(therapist)}</div>
                          <div>ID: {therapist.id}</div>
                        </div>
                      ))}
                    </div>
                    {/* <div >{t('dashboard:table.teamName')}: {item.address?.state || "-"}</div>
                    <div >{t('dashboard:table.tags')}: {item.tags || "-"}</div> */}
                    <div >{t('dashboard:adminTable.startsAt')}: {castDateshort(item.start_before)}</div>
                    <div >{t('dashboard:adminTable.endsAt')}: {castDateshort(
                      new Date(
                        new Date(item.start_before).getTime() + item.duration * 60 * 1000
                      )
                    )}
                    </div>
                    <div>Service: {item.products?.map((product, index) => (
                      <span key={index}>
                        {product.category?.title} {product.duration} min
                      </span>
                    ))}
                    </div>
                    {(item.description || item.message || item.justification) && (
                      <div>{t('dashboard:adminTable.message')}
                        {(() => {
                          const descText = item.description ? `- Description: ${item.description}` : '';
                          const commentText = item.message ? `- Comment: ${item.message}` : '';
                          const jusText = item.justification ? `- Justification: ${item.justification}` : '';
                          const fullText = [descText, commentText, jusText].filter(Boolean).join('\n');
                          const isExpanded = (expandedRows[item.id] || CHUNK_SIZE) >= fullText.length;
                          const displayText = fullText.slice(0, expandedRows[item.id] || CHUNK_SIZE);

                          return (
                            <>
                              {displayText.split('\n').map((line, idx) => (
                                <div key={idx}>{line}</div>
                              ))}
                              {fullText.length > CHUNK_SIZE && (
                                <span
                                  className="show-more"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRow(item.id, fullText.length);
                                  }}
                                  style={{
                                    color: "black",
                                    cursor: "pointer",
                                    marginLeft: 5,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {isExpanded ? " Show Less" : " ... Show More"}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                    <div >{t('dashboard:adminTable.client')}: {item.hotel ? item.hotel?.name : item.client?.extra?.full_name}<br />
                      {item.hotel && (
                        <HotelClientBadges booking={item} t={t} />
                      )}</div>
                    <div >{t('dashboard:adminTable.address')}: {addressFormat(item.address)}</div>
                    <div >{t('dashboard:table.createdAt')}: {castDateshort(item.created_at)}</div>
                    <div >{t('dashboard:adminTable.createdBy')}: {item.hotel ? item.client?.extra?.full_name : item.creator?.extra?.full_name}</div>
                    <div >{item?.device_type ?? 'Safari'} - {item?.device_version ?? '150'}</div>
                    <div>{t('dashboard:adminTable.review')}: ⭐ {item?.extra?.rating}/5</div>
                    <div>{t('dashboard:adminTable.preference')}: {hasFlyproPreference(item) ? t('dashboard:table.yes') : t('dashboard:table.no')}</div>
                    <div className="menu-edit">
                      <IconButton onClick={(event) => { handleClick(event, index) }}>
                        <MoreVertIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedIndex === index}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >

                        <MenuList style={{ minWidth: 150 }}>
                          {item.status === 'COMPLETED' && item.transactions?.length > 0 && item.transactions[0]?.succeeded_at && (
                            <MenuItem onClick={() => { handleClickPDF(item); }}>
                              <ListItemIcon>
                                <DownloadForOfflineIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:downloadBill')}</ListItemText>
                            </MenuItem>

                          )}
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickStatus(e, index);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:modal.bookingDetails.labels.status')}</ListItemText>
                            <ListItemIcon>
                              <ChevronRightIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>

                            <Menu
                              anchorEl={anchorElStatus}
                              open={Boolean(anchorElStatus) && selectedIndex === index}
                              onClose={handleClose}
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                              <MenuList style={{ minWidth: 150 }}>
                                <MenuItem disabled>
                                  <ListItemText primary="Current Status" secondary={asTitles(item.status)} />
                                </MenuItem>
                                <Divider />
                                {Object.entries({
                                  PENDING_ACKNOWLEDGEMENT: 'PENDING ACKNOWLEDGEMENT',
                                  ACKNOWLEDGED: 'ACKNOWLEDGED',
                                  IN_TRANSIT: 'IN TRANSIT',
                                  // STARTED: 'STARTED',
                                  REACHED: 'REACHED',
                                  COMPLETED: 'COMPLETED',
                                  DECLINED: 'DECLINED',
                                  FAILED: 'FAILED',
                                })
                                  .filter(([statusKey]) => statusKey !== item.status)
                                  .map(([statusKey, statusLabel]) => (
                                    <MenuItem
                                      key={statusKey}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmDialogOpen(true);
                                        setPendingStatusChange({ item: item, newStatus: statusKey, justification: item.justification });
                                      }}
                                    >
                                      <ListItemText
                                        primary={asTitles(statusKey)}
                                      />
                                    </MenuItem>
                                  ))}
                              </MenuList>
                            </Menu>
                          </MenuItem>
                          <MenuItem onClick={() => { handleClose(); openDetailsModal(item); }}>
                            <ListItemIcon>
                              <VisibilityIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:viewDetails')}</ListItemText>
                          </MenuItem>
                          <MenuItem onClick={() => { handleClose(); duplicateBooking(item); }}>
                            <ListItemIcon>
                              <FileCopyIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:duplicate')}</ListItemText>
                          </MenuItem>
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') && (
                            <MenuItem onClick={() => {
                              handleClose();
                              if (item.creator?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER')) {
                                setSelectedItem(item);
                                setShowCancelPopup(true);
                              } else {
                                showCancelModal(item);
                              }
                            }}>
                              <ListItemIcon>
                                <CancelIcon style={{ color: "red" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:cancel')}</ListItemText>
                            </MenuItem>
                          )}
                          {renderClientHistoryMenuItem(item)}
                          {renderAdminFlexibleCancelMenuItem(item)}
                          <MenuItem
                            onClick={e => {
                              e.stopPropagation();
                              handleClose();
                              setBookingToEdit(item);
                              setEditModalOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:edits')}</ListItemText>
                          </MenuItem>
                          {canResendPaymentLink(item) && (<MenuItem
                            onClick={e => {
                              e.stopPropagation();
                              handleClose();
                              sendPayLink(item);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:link')}</ListItemText>
                          </MenuItem>
                          )}
                          {item.hotel_id != null && (
                            <MenuItem onClick={() => { handleClose(); openFicheModalHotel(item); }}>
                              <ListItemIcon>
                                <EditIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>Show Hotel Questionnaire</ListItemText>
                            </MenuItem>
                          )}
                          <MenuItem onClick={() => { handleClose(); openFicheModal(item); }}>
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.showClientQuestionnaire')}</ListItemText>
                          </MenuItem>
                          <MenuItem style={{ color: 'red' }} onClick={() => {
                            setSelectedItemId(item.id);
                            setDeleteWarning(getDeleteWarning(item));
                            setOpenDeleteDialog(true);
                          }}>
                            <ListItemIcon>
                              <DeleteIcon style={{ color: "red" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.delete')}</ListItemText>
                          </MenuItem>
                          <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
                            <DialogTitle>{t('dashboard:menu.Cdelete')}</DialogTitle>
                            {deleteWarning && (
                              <p style={{
                                marginTop: "10px",
                                textAlign: "center",
                                color: deleteWarning.includes("!") ? "red" : "orange",
                                fontWeight: "bold"
                              }}>
                                {deleteWarning}
                              </p>
                            )}
                            <DialogContent>
                              {t('dashboard:menu.confirmDelete')}
                              <Button onClick={handleCloseDialog} color="primary">
                                {t('dashboard:cancel_button')}
                              </Button>
                              <Button onClick={handleDeleteConfirmation} style={{ color: "red" }}>
                                {t('dashboard:menu.delete')}
                              </Button>
                            </DialogContent>
                          </Dialog>
                        </MenuList>
                      </Menu>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
          <div className="paginator">
            <select value={perPage} onChange={handlePerPageChange} className="per-page">
              <option value={1}>1</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
            </select>
            <div className="total">Total: {total}</div>
            <div className="pagination">
              <button type="button" disabled={currentPage === 1} onClick={handleFirstPage}>{t('dashboard:first', '<<')}</button>
              <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{'<'}</button>
              <span>{currentPage} / {lastPage}</span>
              <button type="button" disabled={currentPage === lastPage} onClick={handleNextPage}>{'>'}</button>
              <button type="button" disabled={currentPage === lastPage} onClick={handleLastPage}>{t('dashboard:last', '>>')}</button>
            </div>
          </div>
        </div >
      </div >

      <div className="tableau-list view-desktop">
        <div className={`container ${isAdmin ? styles.adminTableContainer : ''}`} style={{ justifyItems: 'center' }}>
          <table>
            <thead>
              {/* --- HOTEL MANAGER HEADER  --- */}
              {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
                <tr>
                  <th id="id" className="sort-header" onClick={handleSort}>{t('dashboard:table.orderId')}</th>
                  <th id="product" className="sort-header" onClick={handleSort}>{t('dashboard:table.product')}</th>
                  <th id="description" className="sort-header" onClick={handleSort}>{t('dashboard:table.description')}</th>
                  <th id="therapist_id" className="sort-header" onClick={handleSort}>{t('dashboard:table.flyingDetails')}</th>
                  {/* <th id="address_id" className="sort-header" onClick={handleSort}>{t('dashboard:table.address')}</th> */}
                  <th id="created_by" className="sort-header" onClick={handleSort}>{t('dashboard:table.createdBy')}</th>
                  <th id="created_at" className="sort-header" onClick={handleSort}>{t('dashboard:table.createdAt')}</th>
                  <th id="start_before" className="sort-header" onClick={handleSort}>{t('dashboard:table.startBefore')}</th>
                  <th className="sort-header" onClick={handleSort}>{t('dashboard:table.completeBefore')}</th>
                  <th id="status" className="sort-header" onClick={handleSort}>{t(`dashboard:table.taskStatus`)}</th>
                  <th>{t('dashboard:table.action')}</th>
                </tr>
              )}

              {/* --- ADMIN HEADER --- */}
              {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
                <tr>
                  <th id="id" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.id')}</th>
                  <th id="team_name" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.team')}</th>
                  <th id="promocode" className="sort-header" onClick={handleSort}>Promo</th>
                  {/* <th id="tags" className="sort-header" onClick={handleSort}>{t('dashboard:table.tags')}</th> */}
                  <th id="product_id" className="sort-header" onClick={handleSort}>{t('dashboard:table.product')}</th>
                  <th id="description" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.message')}</th>
                  <th id="therapist_id" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.flypro')}</th>
                  <th id="bookings_count" className={`sort-header ${styles.bookingCountCell}`} onClick={handleSort}>{t('dashboard:table.bookingCount')}</th>
                  <th id="client_id" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.client')}</th>
                  <th>{t('dashboard:table.vip')}</th>
                  <th id="address_id" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.address')}</th>
                  <th id="created_by" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.createdBy')}</th>
                  <th id="created_at" className="sort-header" onClick={handleSort}>{t('dashboard:table.createdAt')}</th>
                  <th id="start_before" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.startsAt')}</th>
                  <th id="start_before" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.endsAt')}</th>
                  <th id="rating" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.review')}</th>
                  <th id="status" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.status')}</th>
                  <th id="no_preferences" className="sort-header" onClick={handleSort}>{t('dashboard:adminTable.preference')}</th>
                  <th>{t('dashboard:table.action')}</th>
                </tr>

              )}
            </thead>

            {/* --- HOTEL MANAGER BODY --- */}
            {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
              <tbody>
                {translatedBookings?.map((item, index) => (
                  <tr key={index} className="pointer-click" onClick={() => { openDetailsModal(item); }}>
                    <td>{item.id}</td>
                    <td>
                      {(() => {
                        const products = item.products || [];
                        const fullText = products
                          .map(p => `- ${p?.category?.title} ${p?.duration} min`)
                          .join('\n');

                        if (products.length == 1) {
                          return (
                            <div>
                              {fullText}
                            </div>
                          );
                        }
                        const currentSize = expandedRows[`prod_${item.id}`] || CHUNK_SIZE;
                        const isExpanded = currentSize >= fullText.length;
                        const displayText = fullText.slice(0, currentSize);
                        return (
                          <>
                            {displayText.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}

                            {fullText.length > CHUNK_SIZE && (
                              <span
                                className="show-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(`prod_${item.id}`, fullText.length);
                                }}
                                style={{
                                  color: "black",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  fontWeight: "bold",
                                }}
                              >
                                {isExpanded ? " Show Less" : " ... Show More"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        // const descText = item.description ? `- Description: ${item.description}` : '';
                        const commentText = item.message ? `- Comment: ${item.message}` : '';
                        // const jusText = item.justification ? `- Justification: ${item.justification}` : '';
                        const fullText = [commentText].filter(Boolean).join('\n');
                        const isExpanded = (expandedRows[item.id] || CHUNK_SIZE) >= fullText.length;
                        const displayText = fullText.slice(0, expandedRows[item.id] || CHUNK_SIZE);

                        return (
                          <>
                            {displayText.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}
                            {fullText.length > CHUNK_SIZE && (
                              <span
                                className="show-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(item.id, fullText.length);
                                }}
                                style={{
                                  color: "black",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  fontWeight: "bold",
                                }}
                              >
                                {isExpanded ? " Show Less" : " ... Show More"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ whiteSpace: "pre-line" }}>
                      {item.therapists
                        ?.map(
                          (therapist) =>
                            `-  ${t('table.name')}: ${therapist.firstname}`
                        )
                        .flatMap((text, index, array) =>
                          index < array.length - 1
                            ? [text, <br key={index} />]
                            : [text]
                        )}
                    </td>
                    {/* <td>{addressFormat(item.address)}</td> */}
                    <td>{item.client?.roles?.some((role) => role.name !== "ROLE_CLIENT") ? item.hotel?.name : item.client?.extra.full_name}<br />
                      <HotelClientBadges booking={item} t={t} />
                    </td>
                    <td>{castDate(item.created_at).replace(/:\d{2}$/, '')}</td>
                    <td>{castDate(item.start_before).replace(/:\d{2}$/, '')}</td>
                    <td>
                      {castDate(
                        new Date(
                          new Date(item.start_before).getTime() + item.duration * 60 * 1000
                        )
                      ).replace(/:\d{2}$/, '')}</td>
                    <td style={{ textTransform: 'capitalize' }}>
                      <StatusBadge status={item?.status} namespace="statusManager" />
                    </td>
                    <td
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClick(event, index);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedIndex === index}
                        onClose={handleClose}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >

                        <MenuList style={{ minWidth: 150 }}>
                          {/* {item.status == "PAID" && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClickPDF(item);
                              }}
                            >
                              <ListItemIcon>
                                <DownloadForOfflineIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:menu.downloadBill')}</ListItemText>
                            </MenuItem>

                          )} */}
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') &&
                            !item.creator?.roles?.some(role => role.name === 'ROLE_CLIENT') &&
                            !item.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && (
                              <>
                                {(() => {
                                  const now = new Date();
                                  const startTime = new Date(item.start_before);
                                  const endTime = new Date(startTime.getTime() + Number(item.products[0].duration) * 60 * 1000);
                                  const diffHours = (startTime - now) / (1000 * 60 * 60);
                                  const diffHoursEnd = (endTime - now) / (1000 * 60 * 60);
                                  // Only show if startTime is in the future
                                  if (endTime > now) {
                                    if (diffHours > 24) {
                                      return (
                                        <MenuItem
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleClose();
                                            navigate(`/bookings/${item.id}/edit`, { state: { bookingToEdit: item } });
                                          }}
                                        >
                                          <ListItemIcon>
                                            <EditIcon style={{ color: "#1976d2" }} />
                                          </ListItemIcon>
                                          <ListItemText>{t('dashboard:edits')}</ListItemText>
                                        </MenuItem>
                                      );
                                    }
                                    if ((diffHours <= 24 || diffHoursEnd <= 24) && item.products?.length === 1) {
                                      return (
                                        <MenuItem onClick={() => { handleClose(); openProlongModal(item); }}>
                                          <ListItemIcon>
                                            <TimerIcon style={{ color: "#1976d2" }} />
                                          </ListItemIcon>
                                          <ListItemText>{t('commande:prolongation.button')}</ListItemText>
                                        </MenuItem>
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                              </>
                            )}
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              openDetailsModal(item);
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.viewDetails')}</ListItemText>
                          </MenuItem>
                          {!item.creator?.roles?.some(role => role.name === 'ROLE_CLIENT' || role.name === 'ROLE_ADMIN') && (
                            <>
                              {/* {new Date(item.start_before) > new Date(new Date().setHours(23, 59, 59, 999)) && (
                                <MenuItem
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleClose();
                                    setBookingToEdit(item);
                                    setEditModalOpen(true);
                                  }}
                                >
                                  <ListItemIcon>
                                    <EditIcon style={{ color: "#1976d2" }} />
                                  </ListItemIcon>
                                  <ListItemText>Edit Booking</ListItemText>
                                </MenuItem>
                              )} */}
                            </>
                          )}
                          {/* {item.hotel_id != null && (
                            <MenuItem onClick={() => { handleClose(); openFicheModalHotel(item); }}>
                              <ListItemIcon>
                                <EditIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:menu.showHotelQuestionnaire')}</ListItemText>
                            </MenuItem>
                          )} */}
                          {/* {item.client_id != auth?.user?.id && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                                openFicheModal(item);
                              }}
                            >
                              <ListItemIcon>
                                <EditIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:menu.showClientQuestionnaire')}</ListItemText>
                            </MenuItem>
                          )} */}
                          {/* <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              duplicateBooking(item);
                            }}
                          >
                            <ListItemIcon>
                              <FileCopyIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>Duplicate Booking</ListItemText>
                          </MenuItem> */}
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') &&
                            !item.creator?.roles?.some(role => role.name === 'ROLE_CLIENT') &&
                            !item.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && (
                              <>
                                {new Date(item.start_before) > new Date() && (
                                  <MenuItem
                                    style={{ color: "red" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClose();
                                      setSelectedItem(item);
                                      setShowCancelPopup(true);
                                    }}
                                  >
                                    <ListItemIcon>
                                      <CancelIcon style={{ color: "red" }} />
                                    </ListItemIcon>
	                                    <ListItemText>{t('dashboard:cancel')}</ListItemText>
	                                  </MenuItem>
	                                )}
		                                {renderClientHistoryMenuItem(item)}
		                                {renderAdminFlexibleCancelMenuItem(item)}
	                              </>
	                            )}

	                        </MenuList>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}

            {/* --- ADMIN BODY --- */}
            {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
              <tbody>
                {translatedBookings?.map((item, index) => (
                  <tr key={index} className="pointer-click" onClick={() => { openDetailsModal(item); }}>
                    <td >{item.id}</td>
                    <td >{item.address?.state || "-"}</td>
                    {/* <td >{item.tags || "-"}</td> */}
                    <td>
                      <div>{getAdminPromoCode(item) || "-"}</div>
                      <SupplementBadge booking={item} />
                    </td>
                    <td>
                      {(() => {
                        const products = item.products || [];
                        const fullText = products
                          .map(p => `- ${p?.category?.title} ${p?.duration} min`)
                          .join('\n');

                        if (products.length == 1) {
                          return (
                            <div>
                              {fullText}
                            </div>
                          );
                        }
                        const currentSize = expandedRows[`prod_${item.id}`] || CHUNK_SIZE;
                        const isExpanded = currentSize >= fullText.length;
                        const displayText = fullText.slice(0, currentSize);
                        return (
                          <>
                            {displayText.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}

                            {fullText.length > CHUNK_SIZE && (
                              <span
                                className="show-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(`prod_${item.id}`, fullText.length);
                                }}
                                style={{
                                  color: "black",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  fontWeight: "bold",
                                }}
                              >
                                {isExpanded ? " Show Less" : " ... Show More"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const descText = item.description ? `- Description: ${item.description}` : '';
                        const commentText = item.message ? `- Message: ${item.message}` : '';
                        const jusText = item.justification ? `- Justification: ${item.justification}` : '';
                        const fullText = [descText, commentText, jusText].filter(Boolean).join('\n');
                        const isExpanded = (expandedRows[item.id] || CHUNK_SIZE) >= fullText.length;
                        const displayText = fullText.slice(0, expandedRows[item.id] || CHUNK_SIZE);

                        return (
                          <>
                            {displayText.split('\n').map((line, idx) => (
                              <div key={idx}>{line}</div>
                            ))}
                            {fullText.length > CHUNK_SIZE && (
                              <span
                                className="show-more"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(item.id, fullText.length);
                                }}
                                style={{
                                  color: "black",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  fontWeight: "bold",
                                }}
                              >
                                {isExpanded ? " Show Less" : " ... Show More"}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {item.therapists?.map((therapist) => (
                        <div key={therapist.id} className={styles.flyproEntry}>
                          <div>{getFlyproDisplayName(therapist)}</div>
                          <div>ID: {therapist.id}</div>
                        </div>
                      ))}
                    </td>
                    <td className={styles.bookingCountCell}>
                      {(() => {
                        const bookingCount = getClientBookingsCount(item.client_with_bookings_count ?? item.client);

                        return (
                          <span className={styles.bookingCountWithBadge}>
                            <span>{bookingCount ?? '-'}</span>
                            {bookingCount > 0 && isNewClient(item.client) && (
                              <span className={styles.clientFlagBadge}>{t('dashboard:table.newClientValue')}</span>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td >{item.hotel ? item.hotel?.name : item.client?.extra?.full_name}<br />
                      {item.hotel && (
                        <HotelClientBadges booking={item} t={t} />
                      )}</td>
                    <td>
                      {isVipClient(item.client)
                        ? <span className={styles.clientFlagBadge}>{t('dashboard:table.vip')}</span>
                        : '-'}
                    </td>
                    <td >{addressFormat(item.address)}</td>
                    <td>
                      {item.hotel ? item.client?.extra?.full_name : item.creator?.extra?.full_name}<br />
                      - {item?.device_type ?? 'Safari'}-{item?.device_version ?? '150'}
                    </td>
                    <td >{castDate(item.created_at)}</td>
                    <td >{castDate(item.start_before)}</td>
                    <td >
                      {castDate(
                        new Date(
                          new Date(item.start_before).getTime() + item.duration * 60 * 1000
                        )
                      )}
                    </td>
                    <td>⭐ {item?.extra?.rating}/5</td>
                    <td>
                      <StatusBadge
                        status={
                          hasMixedPaidBookingProducts(item)
                            ? 'PENDING_PROLONGATION_PAYMENT'
                            : item?.status
                        }
                      />
                    </td>
                    <td >{hasFlyproPreference(item) ? t('dashboard:table.yes') : t('dashboard:table.no')}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClick(event, index);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedIndex === index}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >

                        <MenuList style={{ minWidth: 150 }}>
                          {item.status === 'COMPLETED' && item.transactions?.length > 0 && item.transactions[0]?.succeeded_at && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClickPDF(item);
                              }}
                            >
                              <ListItemIcon>
                                <DownloadForOfflineIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:menu.downloadBill')}</ListItemText>
                            </MenuItem>

                          )}

                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickStatus(e, index);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:modal.bookingDetails.labels.status')}</ListItemText>
                            <ListItemIcon>
                              <ChevronRightIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>

                            <Menu
                              anchorEl={anchorElStatus}
                              open={Boolean(anchorElStatus) && selectedIndex === index}
                              onClose={handleClose}
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                              <MenuList style={{ minWidth: 150 }}>
                                <MenuItem disabled>
                                  <ListItemText primary="Current Status" secondary={asTitles(item.status)} />
                                </MenuItem>
                                <Divider />
                                {Object.entries({
                                  PENDING_ACKNOWLEDGEMENT: 'PENDING ACKNOWLEDGEMENT',
                                  ACKNOWLEDGED: 'ACKNOWLEDGED',
                                  IN_TRANSIT: 'IN TRANSIT',
                                  // STARTED: 'STARTED',
                                  REACHED: 'REACHED',
                                  COMPLETED: 'COMPLETED',
                                  DECLINED: 'DECLINED',
                                  FAILED: 'FAILED',
                                })
                                  .filter(([statusKey]) => statusKey !== item.status)
                                  .map(([statusKey, statusLabel]) => (
                                    <MenuItem
                                      key={statusKey}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmDialogOpen(true);
                                        setPendingStatusChange({ item: item, newStatus: statusKey, justification: item.justification });
                                      }}
                                    >
                                      <ListItemText
                                        primary={asTitles(statusKey)}
                                      />
                                    </MenuItem>
                                  ))}
                              </MenuList>
                            </Menu>
                          </MenuItem>


                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              openDetailsModal(item);
                            }}
                          >
                            <ListItemIcon>
                              <VisibilityIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.viewDetails')}</ListItemText>
                          </MenuItem>
                          <MenuItem
                            onClick={e => {
                              e.stopPropagation();
                              handleClose();
                              setBookingToEdit(item);
                              setEditModalOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:edits')}</ListItemText>
                          </MenuItem>
                          {canResendPaymentLink(item) && (<MenuItem
                            onClick={e => {
                              e.stopPropagation();
                              sendPayLink(item);
                              handleClose();
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:link')}</ListItemText>
                          </MenuItem>
                          )}
                          {item.hotel_id != null && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                                openFicheModalHotel(item);
                              }}
                            >
                              <ListItemIcon>
                                <EditIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:menu.showHotelQuestionnaire')}</ListItemText>
                            </MenuItem>
                          )}

                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              openFicheModal(item);
                            }}
                          >
                            <ListItemIcon>
                              <EditIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.showClientQuestionnaire')}</ListItemText>
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClose();
                              duplicateBooking(item);
                            }}
                          >
                            <ListItemIcon>
                              <FileCopyIcon style={{ color: "#1976d2" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:duplicate')}</ListItemText>
                          </MenuItem>
                          {!(item.status === 'CANCELED' || item.status === 'DECLINED' || item.status === 'COMPLETED') && (
                            <MenuItem
                              style={{ color: "red" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClose();
                                if (item.creator?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') ||
                                  (item.creator?.roles?.some(role => role.name === 'ROLE_ADMIN') && item.hotel_id != null && !item.client?.roles?.some(role => role.name === 'ROLE_CLIENT'))
                                ) {
                                  setSelectedItem(item);
                                  setShowCancelPopup(true);
                                } else {
                                  showCancelModal(item);
                                }
                              }}
                            >
                              <ListItemIcon>
                                <CancelIcon style={{ color: "red" }} />
                              </ListItemIcon>
                              <ListItemText>{t('dashboard:cancel')}</ListItemText>
                            </MenuItem>
                          )}
                          {renderClientHistoryMenuItem(item)}
                          {renderAdminFlexibleCancelMenuItem(item)}
                          <MenuItem
                            style={{ color: "red" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedItemId(item.id);
                              setDeleteWarning(getDeleteWarning(item));
                              setOpenDeleteDialog(true);
                            }}
                          >
                            <ListItemIcon>
                              <DeleteIcon style={{ color: "red" }} />
                            </ListItemIcon>
                            <ListItemText>{t('dashboard:menu.delete')}</ListItemText>
                          </MenuItem>
                          <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
                            <DialogTitle>{t('dashboard:menu.Cdelete')}</DialogTitle>
                            {deleteWarning && (
                              <p style={{
                                marginTop: "10px",
                                textAlign: "center",
                                color: deleteWarning.includes("!") ? "red" : "orange",
                                fontWeight: "bold"
                              }}>
                                {deleteWarning}
                              </p>
                            )}
                            <DialogContent>
                              {t('dashboard:menu.confirmDelete')}
                              <Button onClick={handleCloseDialog} color="primary">
                                {t('dashboard:close_button')}
                              </Button>
                              <Button onClick={handleDeleteConfirmation} style={{ color: "red" }}>
                                {t('dashboard:menu.delete')}
                              </Button>
                            </DialogContent>
                          </Dialog>
                        </MenuList>
                      </Menu>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}

            <tfoot >
              <tr>
                <td colSpan={15}>
                  <div className="paginator">
                    <select value={perPage} onChange={handlePerPageChange} className="per-page">
                      <option value={1}>1</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                    <div className="total">{t('dashboard:totalRecords')}: {total}</div>
                    <div className="pagination">
                      <button type="button" disabled={currentPage === 1} onClick={handleFirstPage}>{t('dashboard:first', '<<')}</button>
                      <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{'<'}</button>
                      <span>{currentPage} / {lastPage}</span>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleNextPage}>{'>'}</button>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleLastPage}>{t('dashboard:last', '>>')}</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Modal
        isOpen={detailsModalIsOpen}
        onRequestClose={closeDetailsModal}
        style={customStyles}
        contentLabel={t('dashboard:modal.bookingDetails.title')}
      >
        <button className="close-button" onClick={closeDetailsModal}>
          <span className="close-icon">✖</span>
        </button>
        {selectedBooking && (

          <div className="form-inscription mrgBottom" style={{ padding: 0 }}>
            {/* <h2>{t('dashboard:modal.bookingDetails.title')}</h2> */}

            {/* Tab Navigation */}
            <div className="tab-container" style={{ display: 'flex', marginBottom: '30px', justifyContent: 'space-evenly' }}>
              <button
                className={`tab-button ${activeTabModal === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTabModal('details')}
                style={{
                  padding: '20px',
                  border: 'none',
                  background: '#f0f0f0',
                  cursor: 'pointer',
                  fontWeight: activeTabModal === 'details' ? 'bold' : 'normal',
                  color: activeTabModal === 'details' ? '#8fa9b9' : '#000'
                }}
              >
                {t('dashboard:modal.bookingDetails.title')}
              </button>
              <button
                className={`tab-button ${activeTabModal === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTabModal('history')}
                style={{
                  padding: '20px',
                  border: 'none',
                  background: '#f0f0f0',
                  cursor: 'pointer',
                  fontWeight: activeTabModal === 'history' ? 'bold' : 'normal',
                  color: activeTabModal === 'history' ? '#8fa9b9' : '#000'
                }}
              >
                {t('dashboard:modal.bookingHistory')}
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTabModal === 'details' ? (
                <>
                  {/* <p>{t('dashboard:modal.bookingDetails.subtitle')}</p> */}
                  <form>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.orderId')}:</label>
                      <input type="text" name="id" value={selectedBooking.id || '-'} readOnly />
                    </div>
                    {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                      <>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.status')}:</label>
                          <input type="text" name="status" value={asTitles(selectedBooking.status) || '-'} readOnly />
                        </div>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.refundType')}:</label>
                          <input type="text" name="refund_type" value={selectedBooking.refund_type ? t(`refundType.${selectedBooking.refund_type}`) : '-'} readOnly />
                        </div>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">Client:</label>
                          <input type="text" name="client" value={selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') ? selectedBooking?.client?.extra?.full_name : '-'} readOnly />
                        </div>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">Phone:</label>
                          <input type="tel" name="phone" value={ selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') ? (selectedBooking?.phone || selectedBooking?.client?.phone || '-') : '-'} readOnly
                            onClick={() => {
                              if (selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && (selectedBooking.phone || selectedBooking?.client?.phone)) {
                                window.location.href = `tel:${selectedBooking?.phone || selectedBooking?.client?.phone}`;
                              }
                            }}
                            style={{ cursor: (selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && selectedBooking?.phone) ? 'pointer' : 'default' }} />
                        </div>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">Email client:</label>
                          <input type="email" name="email" value={selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') ? selectedBooking?.client?.email : '-'} readOnly
                            onClick={() => {
                              if (selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && selectedBooking.client.email) {
                                window.location.href = `mailto:${selectedBooking?.client?.email}`;
                              }
                            }}
                            style={{ cursor: (selectedBooking.client?.roles?.some(role => role.name === 'ROLE_CLIENT') && selectedBooking?.client?.email) ? 'pointer' : 'default' }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.status')}:</label>
                          <input type="text" name="status_manager" value={t(`statusManager.${selectedBooking.status}`) || '-'} readOnly />
                        </div>
                        {selectedBooking?.client.roles?.some(role => role.name !== 'ROLE_CLIENT') && (
                          <div className="input-hotel-container contain-marge">
                            <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.refundType')}</label>
                            <input type="text" name="refund_type" value={selectedBooking.refund_type ? t(`refundType.${selectedBooking.refund_type}`) : '-'} readOnly />
                          </div>
                        )}
                      </>
                    )}
                    {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
                      <div className="input-hotel-container contain-marge">
                        <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.address')}</label>
                        <input type="text" name="address" value={addressFormat(selectedBooking.address)} readOnly />
                      </div>
                    )}
                    {selectedBooking.hotel && (
                      <div className="input-hotel-container contain-marge">
                        <label className="input-hotel-label">Hotel:</label>
                        <input type="text" name="address_hotel" value={`${selectedBooking.hotel?.name} - ${addressFormat(selectedBooking.hotel?.address)}`} readOnly />
                      </div>
                    )}
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.product')}:</label>
                      <input type="text" name="product" value={selectedBooking.products?.map(
                        (product) =>
                          ` ${product?.category?.title} - ${product.duration} min\n`
                      )} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">Duo:</label>
                      <input type="text" name="duo" value={selectedBooking.therapists?.length === 2 ? 'Oui' : 'Non'} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.duration')}:</label>
                      <input type="text" name="duration" value={`${selectedBooking.duration} min`} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.startBefore')}</label>
                      <input type="text" name="start_before" value={castDate(selectedBooking.start_before)} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.completeBefore')}</label>
                      <input type="text" name="complete_before" value={castDate(new Date(new Date(selectedBooking.start_before).getTime() + selectedBooking.duration * 60 * 1000))} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.description')}:</label>
                      <input type="text" name="message" value={`Message: ${selectedBooking.message || 'N/A'}`} readOnly />
                      {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
                        <>
                          <input type="text" name="description" value={`Description: ${selectedBooking.description || 'N/A'}`} readOnly />
                          <input type="text" name="justification" value={`Justification: ${selectedBooking.justification || 'N/A'}`} readOnly />
                        </>
                      )}
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.flyingDetails')}</label>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '8px',
                          border: 0,
                          borderRadius: '20px',
                          cursor: 'not-allowed',
                          minHeight: '42px',
                          alignContent: 'center',
                          width: '100%',
                          height: '60px',
                          paddingLeft: '40px',
                          fontSize: '14px',
                          lineHeight: '60px',
                          color: '#393d44',
                          background: '#ffffff',
                          position: 'relative',
                          textAlign: 'left',
                        }}
                      >
                        {selectedBooking?.therapists?.length && (
                          selectedBooking.therapists.map((therapist, i) => (
                            <div
                              key={i}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                              {therapist?.media?.[0]?.original_url && (
                                <img
                                  src={therapist.media[0].original_url}
                                  alt={therapist.extra?.full_name || 'Therapist'}
                                  style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                  }}
                                />
                              )}
                              {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                                <span style={{ fontSize: '14px', color: '#333' }}>
                                  {therapist?.extra?.full_name || ''}
                                </span>
                              ) : (
                                <span style={{ fontSize: '14px', color: '#333' }}>
                                  {therapist?.firstname || ''}
                                </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    {/* <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.flyingDetails')}</label>
                      <input type="text" name="flying_details" value={selectedBooking.therapists?.map(
                        (therapist) =>
                          ` Name: ${therapist?.firstname}\n`
                      )} readOnly />
                    </div> */}
                    {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
                      <>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">{t('dashboard:table.productprice')}:</label>
                          <input type="text" name="productprice" value={selectedBooking.products?.map(
                            (product) =>
                              ` ${product.duration} min = ${product.price} CHF\n`
                          )} readOnly />
                        </div>
                        <div className="input-hotel-container contain-marge">
                          <label className="input-hotel-label">Total:</label>
                          <input type="text" name="price" value={getBookingTotalSummary(selectedBooking)} readOnly />
                        </div>
                        <SupplementDetails booking={selectedBooking} />
                      </>
                    )}
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.createdBy')}</label>
                      <input type="text" name="created_by" value={`${selectedBooking.creator?.extra?.full_name} - Roles: ${selectedBooking.creator?.roles?.map(
                        (role) =>
                          `${asTitle(role.name)}\n`
                      )}`} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.createdle')}:</label>
                      <input type="text" name="created_at" value={castDate(selectedBooking.created_at)} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.teamName')}:</label>
                      <input type="text" name="team_name" value={selectedBooking.address?.state || "-"} readOnly />
                    </div>
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:table.tags')}:</label>
                      <input type="text" name="tags" value={selectedBooking.tags || "-"} readOnly />
                    </div>
                    {/* <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.status')}</label>
                      <input type="text" name="status" value={
                        selectedBooking?.products?.every(p => !p.is_paid)
                          ? `${asTitles(selectedBooking?.status)}${selectedBooking?.offline_payment ? ' (Offline Payment)' : ''}`
                          : (selectedBooking?.products?.some(p => !p.is_paid) && selectedBooking?.products?.some(p => p.is_paid))
                            ? asTitles('PENDING_PROLONGATION_PAYMENT')
                            : `${asTitles(selectedBooking?.status)}${selectedBooking?.offline_payment ? ' (Offline Payment)' : ''}`
                      } readOnly />
                    </div> */}
                    <div className="input-hotel-container contain-marge">
                      <label className="input-hotel-label">{t('dashboard:modal.bookingDetails.labels.rating')}</label>
                      <input
                        type="text"
                        name="rating"
                        value={`${"⭐"} ${selectedBooking.extra.rating}/5`}
                        readOnly
                      />
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {/* <div className={stylesCommande.generalInfo}>
                    {t('command.info')}
                  </div> */}
                  <div className={stylesCommande.frameParentHistory}>
                    {selectedBooking?.histories
                      ?.slice()
                      ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      ?.filter(history => history.status !== 'STARTED')
                      ?.filter(history => {
                        if (auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER')) {
                          const translatedStatus = t(`statusManager.${history.status}`);
                          return translatedStatus && translatedStatus !== '-';
                        }
                        return true;
                      })
                      ?.reduceRight((acc, history) => {
                        if (auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER')) {
                          const translatedStatus = t(`statusManager.${history.status}`);
                          const alreadyExists = acc.some(
                            item => t(`statusManager.${item.status}`) === translatedStatus
                          );
                          if (!alreadyExists) acc.push(history);
                        } else {
                          acc.push(history);
                        }
                        return acc;
                      }, [])
                      ?.reverse()
                      ?.map((history, index) => {
                        const statusColors = {
                          COMPLETED: '#45c343',
                          ACKNOWLEDGED: '#4d5ec4',
                          REACHED: '#1c9dff',
                          STARTED: '#8fa9b9',
                          CANCELED: '#FF0000',
                          FAILED: '#FF0000',
                          FAILED_PAYMENT: '#FF0000',
                          DECLINED: '#FF69B4',
                          IN_TRANSIT: '#393d44',
                          CREATED: '#8fa9b9',
                          DEFAULT: '#ff8a00'
                        };

                        const bgColor = statusColors[history.status?.toUpperCase()] || statusColors.DEFAULT;

                        return (
                          <div key={index} className={stylesCommande.frameParent3History} style={{ justifyContent: 'center' }}>
                            <div className={stylesCommande.groupWrapperHistory}>
                              <div className={stylesCommande.ratingWrapperHistory}>
                                <div className={stylesCommande.ratingHistory}>
                                  <div className={stylesCommande.mayContainerHistory}>
                                    <p className={stylesCommande.mayHistory}>
                                      <b>{new Date(history.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</b>
                                    </p>
                                    <p className={stylesCommande.pHistory}>
                                      {new Date(history.created_at).toLocaleDateString('en-GB', { year: 'numeric' })} {new Date(history.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className={stylesCommande.groupContainerHistory}>
                              <div className={stylesCommande.rectangleParentHistory} style={{ width: '260px' }}>
                                <div className={stylesCommande.groupChildHistory} style={{ width: '260px' }} />
                                <div className={stylesCommande.rating13History} style={{ top: '25px', left: 'auto', right: '25px' }}>
                                  <div className={stylesCommande.byApiApiHistory}>
                                    {t('dashboard:by')} {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                                      history.creator?.extra?.full_name || 'System'
                                    ) : (
                                      history.creator?.firstname || 'System'
                                    )}
                                  </div>
                                </div>
                                <div className={stylesCommande.rating14History} style={{ backgroundColor: bgColor, textAlign: 'center', padding: '15px 0', top: '20px', width: '125px' }}>
                                  <div className={stylesCommande.completedHistory}>
                                    {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') ? (
                                      asTitles(history.status)
                                    ) : (
                                      t(`statusManager.${history.status}`)
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
      <Modal
        isOpen={detailsModalIsOpenFiche}
        onRequestClose={closeFichesModal}
        style={customStyles}
      >
        <button className="close-button" onClick={closeFichesModal}>
          <span className="close-icon">✖</span>
        </button>

        {selectedBooking && (
          <div className="form-inscription mrgBottom">
            <div className="tabqa">
              <button className={`button btn-tabs tab ${activeTab === 'form' ? 'active' : ''}`} onClick={() => handleTabClick('form')}>
                {t('dashboard:modal.clientQuestionnaire.tabs.massages')}
              </button>
              <button
                className={`button btn-tabs tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => handleTabClick('info')}>
                {t('dashboard:modal.clientQuestionnaire.tabs.nailCare')}
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'form' && (
                <div className="form-section">
                  <h2>{t('dashboard:modal.clientQuestionnaire.title')} {selectedBooking.client?.extra?.full_name}</h2>
                  <br />
                  <br />
                  <form onSubmit={handleSubmitFiche}>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.parking')}</label>
                      <input type="text" name="parking" value={ficheFormData.parking} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.houseOrBuilding')}</label>
                      <input type="text" name="maison_ou_immeuble" value={ficheFormData.maison_ou_immeuble} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.logisticsInfo')}</label>
                      <input type="text" name="infos_logistiques" value={ficheFormData.infos_logistiques} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.specificTensions')}</label>
                      <input type="text" name="tensions_particulieres" value={ficheFormData.tensions_particulieres} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.idealPressure')}</label>
                      <input type="text" name="pression_ideale" value={ficheFormData.pression_ideale} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.preferredOil')}</label>
                      <input type="text" name="huile_preferee" value={ficheFormData.huile_preferee} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.musicType')}</label>
                      <input type="text" name="type_musique" value={ficheFormData.type_musique} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.healthIssues')}</label>
                      <input type="text" name="problemes_physique" value={ficheFormData.problemes_physique} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.preferredParts')}</label>
                      <input type="text" name="parties_preferees" value={ficheFormData.parties_preferees} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.partsToAvoid')}</label>
                      <input type="text" name="parties_a_ne_pas_masser" value={ficheFormData.parties_a_ne_pas_masser} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.nakedMassage')}</label>
                      <input type="text" name="se_fait_masser_nu" value={ficheFormData.se_fait_masser_nu} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.poorHygiene')}</label>
                      <input type="text" name="mauvaise_hygiene" value={ficheFormData.mauvaise_hygiene} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.inappropriateRemarks')}</label>
                      <input type="text" name="remarque_deplacee" value={ficheFormData.remarque_deplacee} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.approximateAge')}</label>
                      <input type="text" name="age" value={ficheFormData.age} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.profession')}</label>
                      <input type="text" name="type_profession" value={ficheFormData.type_profession} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.referralSource')}</label>
                      <input type="text" name="source" value={ficheFormData.source} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.socialClass')}</label>
                      <input type="text" name="classe_sociale" value={ficheFormData.classe_sociale} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.additionalInfo')}</label>
                      <input type="text" name="info_complementaires" value={ficheFormData.info_complementaires} onChange={handleChangeFiche} />
                    </div>
                    <button type="submit" className="btn-submit">
                      {ficheFormData.id ? 'Update' : 'Save'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'info' && (
                <div className="info-section">
                  <h2>{t('dashboard:modal.clientQuestionnaire.title')} {selectedBooking.client.extra.full_name}</h2>
                  <br />
                  <br />
                  <form onSubmit={handleSubmitFiche}>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.parking')}</label>
                      <input type="text" name="parking_on" value={ficheFormData.parking_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.logisticsInfo')}</label>
                      <input type="text" name="infos_logistiques_on" value={ficheFormData.infos_logistiques_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.houseOrBuilding')}</label>
                      <input type="text" name="maison_ou_immeuble_on" value={ficheFormData.maison_ou_immeuble_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.healthIssues')}</label>
                      <input type="text" name="problemes_physique_on" value={ficheFormData.problemes_physique_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.preferredColor')}</label>
                      <input type="text" name="couleur_on" value={ficheFormData.couleur_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.usualNailShape')}</label>
                      <input type="text" name="forme_on" value={ficheFormData.forme_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.cuticlePreference')}</label>
                      <input type="text" name="cuticules_on" value={ficheFormData.cuticules_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.nailType')}</label>
                      <input type="text" name="ongle_on" value={ficheFormData.ongle_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.approximateAge')}</label>
                      <input type="text" name="age_on" value={ficheFormData.age_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.profession')}</label>
                      <input type="text" name="type_on" value={ficheFormData.type_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.referralSource')}</label>
                      <input type="text" name="entendu_on" value={ficheFormData.entendu_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.clientQuestionnaire.labels.socialClass')}</label>
                      <input type="text" name="classe_on" value={ficheFormData.classe_on} onChange={handleChangeFiche} />
                    </div>
                    <div className="input-hotel-container">
                      <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.additionalInfo')}</label>
                      <input type="text" name="info_on" value={ficheFormData.info_on} onChange={handleChangeFiche} />
                    </div>
                    <button type="submit" className="btn-submit">
                      {ficheFormData.id ? 'Update' : 'Save'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={exportModalIsOpen}
        onRequestClose={closeExportModal}
        contentLabel="Export Modal"
        ariaHideApp={false}
        style={customStylesExp}
      >
        <h2>{t('dashboard:modal.exportBookings.title')}</h2>
        <div className="form-inscription">
          <form onSubmit={handleDownload}>
            <button type='submit' className="btn-submit">{t('dashboard:modal.exportBookings.downloadListing')}</button>
          </form>
        </div>
        {message && <div className="message">{message}</div>}
      </Modal>

      <Modal
        isOpen={detailsModalIsOpenFicheHotel}
        onRequestClose={closeFichesModalHotel}
        style={customStyles}
      >
        <button className="close-button" onClick={closeFichesModalHotel}>
          <span className="close-icon">✖</span>
        </button>
        {selectedHotel && (
          <div className="form-inscription mrgBottom">
            <h2>{t('dashboard:modal.hotelQuestionnaire.title')}: {selectedHotel.contact_first_name}</h2><br />
            <form onSubmit={handleSubmitFicheHotel}>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.parking')}</label>
                <input type="text" name="parking" value={ficheFormDataHotel.parking} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.dressCode')}</label>
                <input type="text" name="tenue_vestimentaire" value={ficheFormDataHotel.tenue_vestimentaire} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.whereToGo')}</label>
                <input type="text" name="ou_se_diriger" value={ficheFormDataHotel.ou_se_diriger} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.howAndWhereToSettle')}</label>
                <input type="text" name="comment_et_ou_sinstaller" value={ficheFormDataHotel.comment_et_ou_sinstaller} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.materialToBring')}</label>
                <input type="text" name="materiel_a_prendre" value={ficheFormDataHotel.materiel_a_prendre} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.materialOnSite')}</label>
                <input type="text" name="materiel_sur_place" value={ficheFormDataHotel.materiel_sur_place} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.servicesProvided')}</label>
                <input type="text" name="prestations_que_lon_peut_fournir" value={ficheFormDataHotel.prestations_que_lon_peut_fournir} onChange={handleChangeFicheHotel} />
              </div>
              <div className="input-hotel-container">
                <label className="input-hotel-label">{t('dashboard:modal.hotelQuestionnaire.labels.additionalInfo')}</label>
                <input type="text" name="infos_complementaires" value={ficheFormDataHotel.infos_complementaires} onChange={handleChangeFicheHotel} />
              </div>
              <button type="submit" className="btn-submit">
                {ficheFormDataHotel.id ? t('dashboard:modal.hotelQuestionnaire.buttons.update') : t('dashboard:modal.hotelQuestionnaire.buttons.save')}
              </button>
            </form>
          </div>
        )}
      </Modal>

      {
        auth?.roles?.some(r => r.name === 'ROLE_ADMIN') && (
          <EditBookingModal
            open={editModalOpen}
            onClose={() => {
              setEditModalOpen(false);
              setBookingToEdit(null);
            }}
            booking={bookingToEdit}
            onSuccess={fetchBookings}
            api={api}
          />
        )
      }

      <AdminFlexibleCancellationModal
        isOpen={showAdminCancelModal}
        booking={bookingForAdminCancel}
        api={api}
        onClose={closeAdminFlexibleCancellation}
        onSuccess={handleAdminFlexibleCancellationSuccess}
      />

      {
        showCancelPopup && selectedItem && (
          <Dialog open={true} onClose={() => setShowCancelPopup(false)}>
            <DialogTitle>{t('cancel_title')}</DialogTitle>
            <DialogContent
              style={{
                textAlign: "center",
              }}>
              {isLessThan24h(selectedItem.start_before) ? (
                <p>
                  {t('cancel_w_24')}
                </p>
              ) : (
                <p>
                  {t('cancel_l_24')}
                </p>
              )}
              <a href="https://flyspa.ch/conditions-annulation/" target="_blank" rel="noopener noreferrer">
                {t('cancel_condition')}
              </a>
            </DialogContent>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px",
                gap: "12px",
              }}
            >
              <button
                onClick={() => {
                  cancelBooking(selectedItem);
                  setShowCancelPopup(false);
                }}
                style={{
                  flex: 1,
                  backgroundColor: "#333",
                  color: "#faef9b",
                  border: "none",
                  borderRadius: "10px",
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t('continue_button')}
              </button>

              <button
                onClick={() => setShowCancelPopup(false)}
                style={{
                  flex: 1,
                  backgroundColor: "#eee",
                  color: "#333",
                  border: "1px solid #ccc",
                  borderRadius: "10px",
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {t('cancel_button')}
              </button>
            </div>
          </Dialog>
        )
      }
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('dashboard:confirmation')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('dashboard:change')} <strong>{asTitles(pendingStatusChange.item?.status)}</strong> {t('dashboard:a')} <strong>{asTitles(pendingStatusChange.newStatus)}</strong>?
          </DialogContentText>
          {(pendingStatusChange.newStatus === "FAILED" || pendingStatusChange.newStatus === "DECLINED") && (
            <TextField
              autoFocus
              margin="dense"
              id="justification"
              label="Justification"
              type="text"
              fullWidth
              multiline
              rows={3}
              value={pendingStatusChange.justification || ''}
              onChange={(e) =>
                setPendingStatusChange((prev) => ({
                  ...prev,
                  justification: e.target.value,
                }))
              }
            />
          )}

        </DialogContent>
        <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
          {t('dashboard:close_button')}
        </Button>
        <Button
          onClick={handleStatusChange}
          color="primary"
          autoFocus
          variant="contained"
        >
          {t('dashboard:confirm')}
        </Button>
      </Dialog>

      <Modal
        isOpen={showCancelConfirm}
        onRequestClose={() => setShowCancelConfirm(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '440px',
            height: 'auto',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
          }
        }}
      >
        {!cancelSuccessMessage ? (
          <>
            <h2>{t('dashboard:cancel_title')}</h2>
            <p style={{ marginTop: '20px' }}>{cancelMessage}</p>
            <a
              href="https://flyspa.ch/conditions-annulation/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '14px', textDecoration: 'underline', display: 'block', marginTop: '10px' }}
            >
              {t('dashboard:cancel_condition')}
            </a>

            {cancelChoicePhase ? (
              <>
                <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px" }}>
                  <ThemeProvider theme={theme}>
                    <Button
                      onClick={() => handleCancelBooking('FULL_REFUND')}
                      variant="contained"
                      color="dark_yellow"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '15px',
                        width: '100%',
                        minWidth: '180px',
                        minHeight: '50px',
                      }}
                    >
                      {t('dashboard:remboursement')}
                    </Button>
                    <Button
                      onClick={() => handleCancelBooking('FLATT_OFF')}
                      variant="contained"
                      color="dark_yellow"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '15px',
                        width: '100%',
                        minWidth: '180px',
                        minHeight: '50px',
                      }}
                    >
                      {t('dashboard:avoir')}
                    </Button>
                  </ThemeProvider>
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px" }}>
                  <ThemeProvider theme={theme}>
                    <Button
                      onClick={() => setShowCancelConfirm(false)}
                      variant="contained"
                      color="dark_grey"
                      sx={{
                        textTransform: 'none',
                        borderRadius: '15px',
                        width: '100%',
                        minWidth: '180px',
                        minHeight: '50px',
                      }}
                    >
                      {t('dashboard:cancel_button')}
                    </Button>
                  </ThemeProvider>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "24px" }}>
                <ThemeProvider theme={theme}>
                  <Button
                    onClick={() => handleCancelBooking('FULL_REFUND')}
                    variant="contained"
                    color="dark_yellow"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '15px',
                      width: '100%',
                      minWidth: '180px',
                      minHeight: '50px',
                    }}
                  >
                    {t('dashboard:continue_button')}
                  </Button>
                  <Button
                    onClick={() => setShowCancelConfirm(false)}
                    variant="contained"
                    color="dark_grey"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '15px',
                      width: '100%',
                      minWidth: '180px',
                      minHeight: '50px',
                    }}
                  >
                    {t('dashboard:close_button')}
                  </Button>
                </ThemeProvider>
              </div>
            )}
          </>
        ) : (
          <>
            <h2>{t('cancel_message')}</h2>
            <p style={{ marginTop: '20px' }}>{cancelSuccessMessage}</p>
            <button
              onClick={() => setShowCancelConfirm(false)}
              style={{
                marginTop: "24px",
                padding: "12px 28px",
                backgroundColor: "#393d44",
                color: "#faef9b",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer"
              }}
            >
              {t('close_button')}
            </button>
          </>
        )}
      </Modal>

      <Modal
        isOpen={showProlongModal}
        onRequestClose={() => {
          setShowProlongModal(false);
          setSelectedProlongProduct(null);
          setExtendedEndTime(null);
          setProlongErrorMessage('');
          setProlongationErrorMessage("");
        }}
        style={{
          content: {
            width: "90%",
            maxWidth: "440px",
            margin: "auto",
            padding: "25px",
            borderRadius: "20px",
            textAlign: "center",
            inset: '50% auto auto 50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            fontFamily: 'Lato',
            backgroundColor: '#fff',
          }
        }}
      >
        <IconButton
          onClick={() => {
            setShowProlongModal(false);
            setSelectedProlongProduct(null);
            setExtendedEndTime(null);
            setProlongErrorMessage('');
            setProlongationErrorMessage("");
          }}
          aria-label="Fermer"
          style={{
            position: 'absolute',
            top: 10,
            left: 5,
            zIndex: 10
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
        <h2 style={{
          fontFamily: 'Marcellus',
          fontWeight: 400,
          fontSize: '18px',
          marginBottom: '25px',
          textAlign: 'center',
          color: '#393D44'
        }}>
          {t('commande:prolongation.title')}
        </h2>
        {prolongErrorMessage && (
          <div style={{ color: 'red', marginBottom: '10px', fontSize: '14px', textAlign: 'center' }}>
            {prolongErrorMessage}
          </div>
        )}


        {/* Duration Selector */}
        <div className="duration-selector-container">
          <div className="duration-selector">
            {availableProlongProducts.length > 0 ? (
              availableProlongProducts.map((p, index) => (
                <button
                  key={p.id}
                  className={`duration-button ${selectedProlongProduct?.id === p.id ? 'selected' : ''}`}
                  onClick={() => {
                    // Exactly like first block
                    const startISO = bookingToProlong?.start_before;
                    const extraDuration = parseInt(p._diffDuration, 10);

                    if (!startISO || !extraDuration || isNaN(extraDuration)) {
                      console.warn(t('commande:prolongation.missing_start_before_or_extra_duration'));
                      return;
                    }

                    setSelectedProlongProduct(p);

                    // Calculate new end time based on originalEndTime + extra minutes
                    const newEndTime = new Date(originalEndTime.getTime() + p._diffDuration * 60000);
                    setExtendedEndTime(newEndTime);
                  }}
                >
                  {p._diffDuration} min.
                </button>
              ))
            ) : (
              <div style={{ color: '#aaa', padding: '10px 5px 10px 18px' }}>
                {t('commande:prolongation.no_prolongation_available')}
              </div>
            )}
          </div>
        </div>

        {/* Date / Time Info */}
        <div className="info-section" style={{ margin: '25px 0' }}>
          <div className="info-line">
            <span className="info-label">{t('commande:prolongation.date')}</span>
            <span className="info-value">
              {bookingToProlong?.start_before
                ? new Date(bookingToProlong.start_before).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  timeZone: 'Europe/Zurich',
                })
                : '—'}
            </span>
          </div>
          <div className="info-line">
            <span className="info-label">{t('commande:prolongation.start')}</span>
            <span className="info-value">
              {bookingToProlong?.start_before
                ? new Date(bookingToProlong.start_before).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZone: 'Europe/Zurich',
                })
                : '—'}
            </span>
          </div>
          <div className="info-line">
            <span className="info-label">{t('commande:prolongation.end')}</span>
            <span className="info-value">
              {extendedEndTime ? (
                <>
                  {extendedEndTime
                    ? extendedEndTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                    : '—'}
                  <span className="info-extra">+{selectedProlongProduct?._diffDuration} minutes</span>
                </>
              ) : (
                '—'
              )}
            </span>
          </div>
        </div>
        {/* Bottom Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '12px', width: '100%' }}>
          <Button
            onClick={() => {
              setSelectedProlongProduct(null);
              setExtendedEndTime(null);
              setProlongErrorMessage('');
              setProlongationErrorMessage("");
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '15px',
              width: '100%',
              minWidth: '180px',
              minHeight: '50px',
              background: '#393D44',
              color: '#FAEF9B'
            }}
          >
            {t('commande:prolongation.reset')}
          </Button>
          <Button
            onClick={handleProlongation}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '15px',
              width: '100%',
              minWidth: '180px',
              minHeight: '50px',
              background: '#393D44',
              color: '#FAEF9B'
            }}
            disabled={isProlongLoading || !selectedProlongProduct}
          >
            {t('commande:prolongation.validate')}
          </Button>
        </div>
        {prolongationErrorMessage && (
          <p style={{ color: "red", textAlign: "center", marginBottom: "10px", marginTop: '12px' }}>
            {prolongationErrorMessage}
          </p>
        )}
      </Modal>



    </>
  );
};

export default Booking;
